// App.js (FULL AND FINAL UNABRIDGED REPLACEMENT)

import './polyfill';
// ⭐ MODIFIED: Added useRef to the React import
import React, { useState, useLayoutEffect, createContext, useContext, Suspense, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app'; // ⭐ NEW: For NFC background tag deep links
import { Geolocation } from '@capacitor/geolocation'; // ⭐ NEW: For capturing location on auto clock-in/out
import { StatusBar, Style } from '@capacitor/status-bar'; // ⭐ NEW IMPORT: Status Bar
// ⭐ NEW IMPORT: Capacitor Community NFC
import { CapacitorNfc } from '@capgo/capacitor-nfc';
import { Preferences } from '@capacitor/preferences'; // ⭐ NEW: Reads native fallback for cold-start NFC launch URLs
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
// import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm'; // ⭐ NEW IMPORT: FCM Plugin for iOS token conversion

import { AuthProvider, useAuth } from './components/context/AuthContext';
import { CurrencyProvider } from './components/context/CurrencyContext';
import { ChatProvider } from './components/context/ChatProvider';
import { ThemeProvider } from './components/context/ThemeContext';
import { SettingsProvider, useSettings } from './components/context/SettingsContext';
// ⭐ 1. IMPORT useSocket here
import { SocketProvider, useSocket } from './components/context/SocketProvider';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import './i18n';

// ⭐ IMPORTS for offline/PWA functionality
import OfflineBanner from './components/common/OfflineBanner';
import ServiceWorkerWrapper from './components/common/ServiceWorkerWrapper';
import ErrorBoundary from './components/common/ErrorBoundary'; 
// ⭐ NEW IMPORT: Region Redirect Modal
import RegionRedirectModal from './components/common/RegionRedirectModal';


import LoginPage from './components/auth/LoginPage';
import SignUpPage from './components/auth/SignUpPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import Sidebar from './components/navigation/Sidebar';
import PublicFormPage from './components/forms/PublicFormPage';
import PublicReviewFormPage from './components/forms/PublicReviewFormPage';
import PublicQuotePage from './pages/PublicQuotePage';
// ⭐ NEW IMPORT: Public Job Photos Page
import PublicJobPhotosPage from './pages/PublicJobPhotosPage';
// ⭐ NEW IMPORT: Public Scratch Card Page
import PublicScratchCardPage from './pages/PublicScratchCardPage';


import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLoadScript } from '@react-google-maps/api';
import './index.css';

import { Bars3Icon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'; // ⭐ MODIFIED: Added SparklesIcon and XMarkIcon

// ⭐ CRITICAL FIX: Imported Loader2 at the VERY TOP to prevent ESLint "Import in body" errors
import { Loader2 } from 'lucide-react';

import api from './utils/api';
import { getQueuedPhotoUploads, removeQueuedPhotoUpload, bumpQueuedPhotoAttempt, buildPhotoFormData } from './utils/offlinePhotoQueue'; // Offline photo queue
import { initializeFirebase } from './firebase';

// ⭐ OFFLINE SYNC: Import toast for sync notifications
import { toast } from 'react-toastify';

// ====================================================================
// ⭐ CRITICAL IMPORTS FOR LANDING PAGES (FIXED LOCATION) ⭐
// NOTE: These are imported statically to prevent runtime import errors during compilation analysis
import LandingPage from './pages/LandingPage';

// Lazy loaded components must be defined after all static imports but before use.
const Verify2FA = React.lazy(() => import('./components/auth/Verify2FA'));
// * ⭐ EXISTING IMPORT FOR AFFILIATE SIGNUP PAGE *
const AffiliateSignUpPage = React.lazy(() => import('./components/auth/AffiliateSignUpPage'));
// * ⭐ NEW IMPORT FOR FRANCHISOR SIGNUP PAGE *
const FranchisorSignUpPage = React.lazy(() => import('./components/auth/FranchisorSignUpPage'));
// * ⭐ END NEW IMPORT *
// * ⭐ CRITICAL IMPORT FOR RESET PAGE *
const ResetPasswordPage = React.lazy(() => import('./components/auth/ResetPasswordPage'));

// ⭐ NEW IMPORT: Mobile App Download Page ⭐
const DownloadAppPage = React.lazy(() => import('./pages/DownloadAppPage'));

// ⭐ NEW IMPORT: Payment Redirect Bridge ⭐
const PaymentRedirect = React.lazy(() => import('./components/payments/PaymentRedirect'));

// Lazy loaded components - Industry Landing Pages
const PlumbingLandingPage = React.lazy(() => import('./pages/PlumbingLandingPage'));
const ElectricianLandingPage = React.lazy(() => import('./pages/ElectricianLandingPage'));
const LandscapingLandingPage = React.lazy(() => import('./pages/LandscapingLandingPage'));
const HvacLandingPage = React.lazy(() => import('./pages/HvacLandingPage'));
const PropertyManagementLandingPage = React.lazy(() => import('./pages/PropertyManagementLandingPage'));
const SecurityLandingPage = React.lazy(() => import('./pages/SecurityLandingPage')); // <*** NEW SECURITY IMPORT
// ====================================================================

const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const CustomerPage = React.lazy(() => import('./components/customers/CustomerPage'));
const LeadsView = React.lazy(() => import('./components/customers/LeadsView'));
const SettingsPage = React.lazy(() => import('./components/settings/SettingsPage'));

// ⭐ NEW IMPORT FOR PARTNER DIRECTORY PAGE ⭐
const PartnerDirectoryPage = React.lazy(() => import('./pages/PartnerDirectoryPage'));
// ⭐ NEW IMPORT FOR PUBLIC DIRECTORY LISTING ⭐
const PublicDirectoryListing = React.lazy(() => import('./pages/PublicDirectoryListing'));

// ⭐ NEW IMPORT FOR SUPPORT PAGE ⭐
const SupportPage = React.lazy(() => import('./components/support/SupportPage'));
const EmailTemplatesView = React.lazy(() => import('./components/email-templates/EmailTemplatesView'));
const FormBuilderPage = React.lazy(() => import('./components/forms/FormBuilderPage'));
const StaffPage = React.lazy(() => import('./components/staff/StaffPage'));
// * ⭐ NEW IMPORT FOR TIME CLOCK PAGE *
const TimeClockReportPage = React.lazy(() => import('./components/staff/TimeClockReportPage'));
const DailyAttendancePage = React.lazy(() => import('./components/staff/DailyAttendancePage'));
// * ⭐ NEW IMPORT FOR PERFORMANCE DASHBOARD *
const StaffPerformanceDashboard = React.lazy(() => import('./components/staff/StaffPerformanceDashboard'));
const SchedulerView = React.lazy(() => import('./components/scheduler/SchedulerView'));
const StockView = React.lazy(() => import('./components/stock/StockView'));
const AssetsRenewalsPage = React.lazy(() => import('./components/assets-renewals/AssetsRenewalsPage'));
const CoshhSheetsPage = React.lazy(() => import('./components/coshh/CoshhSheetsPage')); // ⭐ NEW: COSHH sheets + QR codes
const SiteAssessmentsPage = React.lazy(() => import('./components/forms/SiteAssessmentsPage')); // ⭐ NEW: Standalone Site Assessments
const MarketingReelsPage = React.lazy(() => import('./components/marketing/MarketingReelsPage')); // ⭐ NEW: Marketing Reels page
const QuoteFormsPage = React.lazy(() => import('./components/marketing/QuoteFormsPage')); // ⭐ NEW: Quote Forms admin page
const PublicQuoteFormPage = React.lazy(() => import('./components/public/PublicQuoteFormPage')); // ⭐ NEW: public stepped quote form
const LandingPagesPage = React.lazy(() => import('./components/marketing/LandingPagesPage')); // ⭐ NEW: Landing Pages admin page
const PublicLandingPage = React.lazy(() => import('./components/public/PublicLandingPage')); // ⭐ NEW: public landing page (/page/:slug)
const SpotCheckerPage = React.lazy(() => import('./components/dashboard/SpotCheckerPage'));
const RoutePlannerView = React.lazy(() => import('./components/route-planner/RoutePlannerView'));
const StaffAbsencePage = React.lazy(() => import('./components/staff/StaffAbsencePage'));
const AbsenceReportingPage = React.lazy(() => import('./components/staff/AbsenceReportingPage'));
const StaffMemberAbsencePage = React.lazy(() => import('./components/staff/StaffMemberAbsencePage'));
const StaffMemberJobsPage = React.lazy(() => import('./components/staff/StaffMemberJobsPage'));
const InvoicePage = React.lazy(() => import('./components/invoices/InvoicePage'));
const InvoiceDetails = React.lazy(() => import('./components/invoices/InvoiceDetails'));
const TipsReportPage = React.lazy(() => import('./components/invoices/TipsReportPage')); // ⭐ NEW: customer tips
const PayrollPage = React.lazy(() => import('./components/payroll/PayrollPage'));
const CommissionReportPage = React.lazy(() => import('./components/reports/CommissionReportPage'));
const FinancialDashboardPage = React.lazy(() => import('./components/dashboard/FinancialDashboardPage'));
// ⭐ NEW IMPORT FOR COMPANY HEALTH ⭐
const CompanyHealthDashboard = React.lazy(() => import('./components/finance/CompanyHealthDashboard'));
const JobsPage = React.lazy(() => import('./components/jobs/JobsPage'));
const JobDetailsPage = React.lazy(() => import('./components/jobs/JobDetailsPage'));
const AdminChatPage = React.lazy(() => import('./components/chat/AdminChatPage'));
const StaffChatPage = React.lazy(() => import('./components/chat/StaffChatPage'));
const CustomerChatPage = React.lazy(() => import('./components/chat/CustomerChatPage'));
const CustomerJobHistoryPage = React.lazy(() => import('./components/customerPortal/CustomerJobHistoryPage'));
const CustomerJobDetailsPage = React.lazy(() => import('./components/customerPortal/CustomerJobDetailsPage'));
const CustomerInvoiceDetails = React.lazy(() => import('./components/customerPortal/CustomerInvoiceDetails'));
const CustomerQuoteRequestPage = React.lazy(() => import('./components/customerPortal/CustomerQuoteRequestPage'));
const CustomerQuotesPage = React.lazy(() => import('./components/customerPortal/CustomerQuotesPage'));
const CustomerQuoteDetailsPage = React.lazy(() => import('./components/customerPortal/CustomerQuoteDetailsPage'));
const CustomerDashboard = React.lazy(() => import('./components/customerPortal/CustomerDashboard'));
const CustomerInvoicesPage = React.lazy(() => import('./components/customerPortal/CustomerInvoicesPage'));
const CustomerAppointmentsPage = React.lazy(() => import('./components/customerPortal/CustomerAppointmentsPage'));
const CustomerEmergencyPage = React.lazy(() => import('./components/customerPortal/CustomerEmergencyPage'));
const CustomerContractsPage = React.lazy(() => import('./components/customerPortal/CustomerContractsPage'));
const CustomerContractDetailsPage = React.lazy(() => import('./components/customerPortal/CustomerContractDetailsPage'));
// ⭐ NEW: Customer Loyalty Page ⭐
const CustomerLoyaltyPage = React.lazy(() => import('./components/customerPortal/CustomerLoyaltyPage'));


const StaffDashboard = React.lazy(() => import('./components/staffPortal/StaffDashboard'));
const StaffSchedulePage = React.lazy(() => import('./components/staffPortal/StaffSchedulePage'));
const MyPayslipsPage = React.lazy(() => import('./components/staffPortal/MyPayslipsPage'));
const MyHoursPage = React.lazy(() => import('./components/staff/MyHoursPage')); // ⭐ NEW: Decline hours review
const MyAttendancePage = React.lazy(() => import('./components/staff/MyAttendancePage')); // ⭐ NEW: Decline daily clock attendance review
const StaffContractsPage = React.lazy(() => import('./components/staffPortal/StaffContractsPage'));
const StaffContractDetailsPage = React.lazy(() => import('./components/staffPortal/StaffContractDetailsPage'));
// ⭐ NEW: Staff Invoices Page ⭐
const StaffMyInvoices = React.lazy(() => import('./components/staff/StaffMyInvoices'));
// ⭐ NEW: Staff Invoice Details Page ⭐
const StaffInvoiceDetails = React.lazy(() => import('./components/staff/StaffInvoiceDetails'));
// ⭐ NEW: Staff Reviews Page ⭐
const StaffReviewsPage = React.lazy(() => import('./components/staff/StaffReviewsPage'));
// ⭐ NEW: Staff Rewards Page ⭐
const StaffRewardsPage = React.lazy(() => import('./components/staff/StaffRewardsPage'));
// ⭐ NEW: Staff Stock Request Page ⭐
const StaffStockRequestPage = React.lazy(() => import('./components/staffPortal/StaffStockRequestPage'));

const QuotesPage = React.lazy(() => import('./components/dashboard/QuotesPage'));
const AdminQuoteGenerationPage = React.lazy(() => import('./views/AdminQuoteGenerationPage'));
const EmailMarketingPage = React.lazy(() => import('./views/EmailMarketingPage'));
const SmsMarketingPage = React.lazy(() => import('./views/SmsMarketingPage'));
const PushNotificationMarketingPage = React.lazy(() => import('./views/PushNotificationMarketingPage'));
const FunnelPage = React.lazy(() => import('./views/Funnel'));
const LandingPageBuilder = React.lazy(() => import('./views/LandingPageBuilder'));
const AdminExpenseDashboard = React.lazy(() => import('./components/expenses/AdminExpenseDashboard'));
const StaffExpensesPage = React.lazy(() => import('./components/staffPortal/StaffExpensesPage'));

// ⭐ NEW IMPORT: Fleet Dashboard ⭐
const FleetDashboard = React.lazy(() => import('./components/fleet/FleetDashboard'));

const MyAbsenceRequestsPage = React.lazy(() => import('./components/staff/MyAbsenceRequestsPage'));
const OwnerDashboard = React.lazy(() => import('./components/admin/OwnerDashboard'));
const KnowledgeBasePage = React.lazy(() => import('./views/KnowledgeBasePage'));
const PricingPage = React.lazy(() => import('./components/auth/PricingPage'));
const SubscriptionSuccessPage = React.lazy(() => import('./components/auth/SubscriptionSuccessPage'));
// * ⭐ CRITICAL IMPORT FOR CHATBOT CUSTOMIZER & EMBEDDED PAGE (FIXED ERROR HERE) *
const ChatbotCustomizer = React.lazy(() => import('./components/chatbot/ChatbotCustomizer')); 
const EmbeddedChatbotPage = React.lazy(() => import('./components/chatbot/EmbeddedChatbotPage')); 
// ⭐⭐⭐ NEW: LANDING PAGE BOT EMBED WRAPPER ⭐⭐⭐
const EmbeddedLandingBotPage = React.lazy(() => import('./components/chatbot/EmbeddedLandingBotPage'));

// ⭐ NEW IMPORT: Reward Lottery Customizer ⭐
const LotteryCustomizer = React.lazy(() => import('./components/admin/LotteryCustomizer'));

// * ⭐ NEW IMPORT FOR ADMIN QUOTE CALCULATOR PAGE (views/QuoteCalculatorPage) *
const QuoteCalculatorPage = React.lazy(() => import('./views/QuoteCalculatorPage'));
// * ⭐ NEW IMPORT FOR STAFF QUOTE CALCULATOR PAGE (components/staffPortal/StaffQuoteCalculatorPage) *
const StaffQuoteCalculatorPage = React.lazy(() => import('./components/staffPortal/StaffQuoteCalculatorPage'));
// * ⭐ NEW IMPORT FOR ADMIN CHATBOT WIDGET *
const AdminChatbotWidget = React.lazy(() => import('./components/chatbot/AdminChatbotWidget'));
// ⭐ NEW IMPORTS FOR STAFF TRAINING MODULES ⭐
const StaffTrainingDashboard = React.lazy(() => import('./components/staffPortal/StaffTrainingDashboard'));
const StaffTrainingView = React.lazy(() => import('./components/staffPortal/StaffTrainingView'));
// ⭐ NEW IMPORTS FOR ADMIN TRAINING MODULES ⭐
const AdminTrainingDashboard = React.lazy(() => import('./components/admin/AdminTrainingDashboard'));
const AdminModuleEditor = React.lazy(() => import('./components/admin/AdminModuleEditor'));
// ⭐ CRITICAL: IMPORT THE RESULTS TABLE PAGE ⭐
const TrainingResultsTable = React.lazy(() => import('./components/admin/TrainingResultsTable'));
// ⭐ NEW IMPORT: Scratch Card Manager ⭐
const ScratchCardManager = React.lazy(() => import('./components/discounts/ScratchCardManager'));
// ⭐ NEW IMPORTS FOR RECRUITMENT/ONBOARDING ⭐
const CareersHub = React.lazy(() => import('./components/public/CareersHub')); // ⭐ NEW: Careers Hub 
const JobApplicationForm = React.lazy(() => import('./components/public/JobApplicationForm'));
const ApplicantDashboard = React.lazy(() => import('./components/admin/ApplicantDashboard'));
// ⭐ NEW IMPORT FOR PUBLIC ASSESSMENT ⭐
const PublicAssessmentView = React.lazy(() => import('./components/public/PublicAssessmentView'));
// ==========================================================
// ⭐ EXISTING IMPORT: Affiliate Dashboard Component ⭐
const AffiliateDashboard = React.lazy(() => import('./components/affiliates/AffiliateDashboard'));
// ⭐ NEW IMPORT: Franchisor Dashboard Component ⭐
const FranchisorDashboard = React.lazy(() => import('./components/franchisors/FranchisorDashboard'));
// ⭐ NEW IMPORT: Franchisor Pricing Page ⭐
const FranchisorPricingPage = React.lazy(() => import('./components/franchisors/FranchisorPricingPage'));
// ==========================================================

// ⭐ NEW IMPORT: Smart Booking Form ⭐
const SmartBookingForm = React.lazy(() => import('./components/forms/SmartBookingForm'));
// ⭐ NEW IMPORT: Public Smart Booking Page ⭐
const PublicSmartBookingPage = React.lazy(() => import('./pages/PublicSmartBookingPage'));
// ⭐ NEW: Generic Book Online page (service -> about the job -> how often -> when -> details)
const BookOnlinePage = React.lazy(() => import('./components/booking/BookOnlinePage'));

// ⭐ CRITICAL: IMPORT BOOKING BUDDY HERE ⭐
const BookingBuddy = React.lazy(() => import('./components/chatbot/BookingBuddy'));


const googleMapsLibraries = ['places'];
export const queryClient = new QueryClient();
export const MapsApiContext = createContext(null);

// ====================================================================
// ⭐ OFFLINE SYNC MANAGER LOGIC ⭐
// (Content of OfflineSyncManager remains unchanged)
// ====================================================================

// This key stores an ARRAY of pending actions, like a queue.
const PENDING_ACTIONS_KEY = 'pendingOfflineActions';
// ⭐ OFFLINE SYNC FIX: Key for storing job *snapshot* updates
const PENDING_JOBS_KEY = 'pendingOfflineJobUpdates';
// ⭐ FIX: Cap on retry attempts for an action that keeps failing with a network
// error or 5xx. Without this, an action whose failure will never resolve on
// retry (e.g. a malformed payload that crashes the server every time) would
// loop silently forever — "saved and will sync later" but never actually
// telling the staff member it never went through.
const MAX_SYNC_ATTEMPTS = 5;

/**
 * Retrieves the queue of pending actions from localStorage.
 * @returns {Array<object>} An array of action objects.
 */
const getPendingActions = () => {
    try {
        return JSON.parse(localStorage.getItem(PENDING_ACTIONS_KEY)) || [];
    } catch (e) {
        console.error("Failed to parse pending actions:", e);
        localStorage.removeItem(PENDING_ACTIONS_KEY); // Clear corrupted data
        return [];
    }
};

/**
 * Saves a new action to the end of the queue.
 * @param {object} action - The action to save (e.g., { type: 'clock-in', ... })
 */
const savePendingAction = (action) => {
    try {
        const actions = getPendingActions();
        // Add a unique ID to the action so we can remove it later
        action.actionId = `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // ⭐ FIX: Start the retry counter at 0 for every newly queued action.
        action.attemptCount = 0;
        actions.push(action);
        localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(actions));
    } catch (e) {
        console.error("Failed to save pending action:", e);
        toast.error("Could not save offline changes. Storage might be full.");
    }
};

/**
 * ⭐ FIX: Increments and persists the retry counter for one action in the queue,
 * without touching any other pending action. Returns the new count, or null if
 * the action was no longer in the queue (e.g. removed by a concurrent tab).
 * @param {string} actionId
 */
const incrementActionAttempt = (actionId) => {
    if (!actionId) return null;
    try {
        const actions = getPendingActions();
        const idx = actions.findIndex(a => a.actionId === actionId);
        if (idx === -1) return null;
        actions[idx].attemptCount = (actions[idx].attemptCount || 0) + 1;
        localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(actions));
        return actions[idx].attemptCount;
    } catch (e) {
        console.error("Failed to update action attempt count:", e);
        return null;
    }
};

/**
 * Removes a specific action from the queue (e.g., after it syncs successfully).
 * @param {string} actionId - The unique ID of the action to remove.
 */
const removePendingAction = (actionId) => {
    if (!actionId) return;
    try {
        let actions = getPendingActions();
        actions = actions.filter(a => a.actionId !== actionId);
        localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(actions));
    } catch (e) {
        console.error("Failed to remove pending action:", e);
    }
};

// ⭐ OFFLINE SYNC FIX: Copied helper functions from StaffJobCard.js

/**
 * ⭐ OFFLINE SYNC FIX
 * Creates a unique key for a job instance to use in localStorage.
 * @param {object} job - The job object (only needs _id and parentJob).
 * @param {string} instanceDate - The specific instance date.
 * @returns {string} A unique key, e.g., "pendingJob_parentJobId_instanceDate"
 */
const getPendingJobKey = (job, instanceDate) => {
    if (!job || !instanceDate) return null;
    // Use the parentJob ID if it's a child, or its own ID if it's a parent/non-recurring
    const parentId = job.parentJob || job._id; 
    // Format instanceDate to a consistent string (YYYY-MM-DD) to avoid timezone issues in the key
    const dateStr = new Date(instanceDate).toISOString().split('T')[0];
    return `pendingJob_${parentId}_${dateStr}`;
};

/**
 * ⭐ OFFLINE SYNC FIX
 * Retrieves all pending job *snapshot* updates from localStorage.
 * @returns {object} An object where keys are jobKeys and values are job objects.
 */
const getPendingUpdates = () => {
    try {
        return JSON.parse(localStorage.getItem(PENDING_JOBS_KEY)) || {};
    } catch (e) {
        console.error("Failed to parse pending updates:", e);
        localStorage.removeItem(PENDING_JOBS_KEY); // Clear corrupted data
        return {};
    }
};

/**
 * ⭐ OFFLINE SYNC FIX
 * Clears a pending job *snapshot* update from localStorage after a successful API sync.
 * @param {string} jobKey - The unique key for this job instance.
 */
const clearPendingUpdate = (jobKey) => {
    if (!jobKey) return;
    try {
        const updates = getPendingUpdates();
        if (updates[jobKey]) {
            console.log(`[OfflineSyncManager] Clearing snapshot for ${jobKey}`); // Log the fix
            delete updates[jobKey];
            localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(updates));
        }
    } catch (e) {
        console.error("Failed to clear pending update:", e);
    }
};

/**
 * ⭐ FIX: Fallback for synced actions that don't carry an instanceDate (e.g. the
 * NFC deep-link clockin-failed/clockout-failed offline branches in
 * handleNfcDeepLink, which queue via savePendingAction with no instanceDate).
 * getPendingJobKey() returns null without an instanceDate, so clearPendingUpdate()
 * silently no-ops for those actions — leaving a stale snapshot in
 * pendingOfflineJobUpdates that StaffJobCard's mount-restore effect keeps
 * re-applying on every relaunch, indefinitely (this used to get wiped every
 * relaunch by AuthContext's old unconditional clearJobLocalState() call, which
 * masked it). This clears any snapshot for the job regardless of which date
 * it was keyed under.
 */
const clearPendingUpdatesForJob = (jobId) => {
    if (!jobId) return;
    try {
        const updates = getPendingUpdates();
        const prefix = `pendingJob_${jobId}_`;
        let changed = false;
        Object.keys(updates).forEach(key => {
            if (key.startsWith(prefix)) {
                console.log(`[OfflineSyncManager] Clearing snapshot (fallback) for ${key}`);
                delete updates[key];
                changed = true;
            }
        });
        if (changed) {
            localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(updates));
        }
    } catch (e) {
        console.error("Failed to clear pending updates for job:", e);
    }
};
// ⭐ END OF COPIED HELPER FUNCTIONS


/**
 * The Sync Manager component.
 * This component listens for the app to come online and tries to sync pending actions.
 */
const OfflineSyncManager = () => {
    const { user } = useAuth();
    // ⭐ 2. GET the socket instance from the context
    const socket = useSocket(); 
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // ⭐ 3. RECONNECT FIX: Ensure socket is present and not currently connecting
            if (socket && !socket.connected) {
                console.log('[OfflineSyncManager] Back online, forcing socket to connect...');
                // Checking if it is already connecting to avoid race conditions
                socket.connect();
            }
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            // ⭐ 4. OFFLINE FIX: Only disconnect if actually connected.
            // Explicit disconnection prevents some "closed before established" errors when network flickers.
            if (socket && socket.connected) {
                console.log('[OfflineSyncManager] Went offline, forcing socket to disconnect...');
                socket.disconnect();
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [socket]); // ⭐ 5. ADD socket to the dependency array

    const syncPendingActions = useCallback(async () => {
        if (isSyncing || !user) return; // Don't sync if already syncing or not logged in
        
        const actionsToSync = getPendingActions();
        // Photos are queued separately (IndexedDB, see utils/offlinePhotoQueue.js).
        const photosToSync = await getQueuedPhotoUploads();
        if (actionsToSync.length === 0 && photosToSync.length === 0) return;

        setIsSyncing(true);
        const totalToSync = actionsToSync.length + photosToSync.length;
        toast.info(`Back online! Syncing ${totalToSync} offline change(s)...`, { autoClose: 2000 });

        let successCount = 0;
        let failCount = 0;

        // ⭐ BUGFIX: Track jobs that suffer a critical sync failure in this batch
        const failedJobIds = new Set();
        // Set when the action loop stops early for a connection/token problem,
        // so the photo uploads below don't try (and fail) the same round.
        let stoppedEarly = false;

        // We must process actions one-by-one (serially)
        // to prevent race conditions (e.g., clock-out before clock-in).
        for (const action of actionsToSync) {
            
            // ⭐ BUGFIX: Check if a previous action for this job already failed permanently
            // If so, skip and delete this dependent action to prevent "you are not clocked in" errors
            if (action.jobId && failedJobIds.has(action.jobId)) {
                console.warn(`[OfflineSyncManager] Skipping and removing dependent action ${action.type} for job ${action.jobId} due to a prior failure in the queue.`);
                removePendingAction(action.actionId);
                failCount++;
                continue;
            }

            try {
                console.log(`[OfflineSyncManager] Syncing action: ${action.type} for job ${action.jobId}`);
                // By spreading action.payload, the server will use the original offline timestamp.
                switch (action.type) {
                    // ⭐ NEW CASES: Handle Daily Clock In/Out syncing
                    case 'daily-clock-in':
                        await api.post(action.endpoint || '/staff/daily-clock-in', action.payload);
                        break;
                    case 'daily-clock-out':
                        await api.post(action.endpoint || '/staff/daily-clock-out', action.payload);
                        break;
                    // Existing job-level actions
                    case 'clock-in':
                        await api.put(`/jobs/${action.jobId}/clock-in`, action.payload);
                        break;
                    case 'clock-out':
                        await api.put(`/jobs/${action.jobId}/clock-out`, action.payload);
                        break;
                    // payload carries clientTimestamp (the real tap time). Older
                    // queued breaks have no payload and fall back to sync time.
                    case 'start-break':
                        await api.post(`/jobs/${action.jobId}/break-start`, action.payload || {});
                        break;
                    case 'end-break':
                        await api.post(`/jobs/${action.jobId}/break-end`, action.payload || {});
                        break;
                    case 'post-comment':
                        await api.post(`/jobs/${action.jobId}/comments`, action.payload);
                        break;
                    case 'save-survey':
                        await api.put(`/jobs/${action.jobId}`, action.payload);
                        break;
                    case 'skip-journey':
                         // Note: 'arrived: true' is in the payload
                         await api.put(`/staff-portal/jobs/${action.jobId}/live-location`, { ...action.payload });
                        break;
                    case 'complete-individual':
                        await api.post(`/jobs/${action.jobId}/complete-individual`, action.payload);
                        break;
                    case 'complete-occurrence':
                         await api.post(`/jobs/${action.jobId}/complete-occurrence`, action.payload);
                        break;
                    case 'finalize-survey':
                         await api.post(`/jobs/${action.jobId}/finalize-survey`);
                        break;
                    case 'notify-late':
                         await api.post(`/jobs/${action.jobId}/notify-late`, action.payload);
                        break;
                    // ⭐ NEW CASE: Handle offline job completion with stock return
                    case 'return-stock-and-complete':
                         await api.post(`/jobs/${action.jobId}/return-stock`, action.payload);
                        break;
                    default:
                        console.warn(`[OfflineSyncManager] Unknown action type: ${action.type}. Skipping.`);
                        break;
                }
                
                // If successful, remove it from the action queue
                removePendingAction(action.actionId);
                successCount++;

                // After the *action* is synced, we MUST clear the *snapshot*.
                // This prevents the UI from reverting to the old offline state on reload.
                const successKeyJobId = action.parentJobId || action.jobId;
                if (action.instanceDate) {
                    if (successKeyJobId) {
                        const jobStub = { _id: successKeyJobId, parentJob: successKeyJobId };
                        const jobKey = getPendingJobKey(jobStub, action.instanceDate);
                        clearPendingUpdate(jobKey);
                    }
                } else if (successKeyJobId) {
                    // ⭐ FIX: no instanceDate on this action (e.g. NFC deep-link
                    // clockin-failed/clockout-failed queuing) — sweep by jobId instead
                    // of silently leaving a stale snapshot behind.
                    clearPendingUpdatesForJob(successKeyJobId);
                }

            } catch (err) {
                console.error(`[OfflineSyncManager] Failed to sync action ${action.actionId} (Type: ${action.type}):`, err);
                
                // ⭐ FIX: A CSRF "stale token" 403 is NOT a problem with the clock-in itself -
                // it's the website's security token being out of date. api.js already
                // fetches a fresh token and retries once; if that still fails, KEEP the
                // action and try again later instead of deleting it (this used to wipe
                // offline clock-ins for staff using the website on their phone).
                const isCsrfRejection = err.response && err.response.status === 403 && (
                    err.response.data?.code === 'EBADCSRFTOKEN' ||
                    /csrf|security token/i.test(String(err.response.data?.message || ''))
                );
                if (isCsrfRejection) {
                    console.warn(`[OfflineSyncManager] CSRF rejected ${action.type} for job ${action.jobId}. Keeping it and retrying later.`);
                    stoppedEarly = true;
                    break;
                }

                // If the error is a server conflict (e.g., 400, 409) or auth error (401, 403)
                // it's likely a permanent failure. We'll remove it to prevent sync loops.
                if (err.response && err.response.status >= 400 && err.response.status < 500) {
                    toast.error(`A saved offline action failed to sync: ${err.response?.data?.message || 'Please redo this action manually.'}.`);
                    removePendingAction(action.actionId); // Remove bad action
                    
                    // ⭐ BUGFIX: Register this job as failed so future queue items for it are safely deleted
                    if (action.jobId) {
                        failedJobIds.add(action.jobId);
                    }
                    
                    // Also clear the snapshot for the failed action
                    const failKeyJobId = action.parentJobId || action.jobId;
                    if (action.instanceDate) {
                         if (failKeyJobId) {
                             const jobStub = { _id: failKeyJobId, parentJob: failKeyJobId };
                             const jobKey = getPendingJobKey(jobStub, action.instanceDate);
                             clearPendingUpdate(jobKey);
                         }
                    } else if (failKeyJobId) {
                        // ⭐ FIX: same fallback as the success path above.
                        clearPendingUpdatesForJob(failKeyJobId);
                    }
                    failCount++;
                } else {
                    // Network error or 5xx server error.
                    // ⭐ FIX: Don't retry forever. Increment this action's attempt
                    // count; once it's failed MAX_SYNC_ATTEMPTS times, treat it as
                    // a permanent failure (surface it, remove it) instead of
                    // silently looping every 3s indefinitely — this is what
                    // protects against a non-recoverable 5xx (e.g. a malformed
                    // payload that crashes the server the same way every time).
                    const attempts = incrementActionAttempt(action.actionId);
                    if (attempts !== null && attempts >= MAX_SYNC_ATTEMPTS) {
                        toast.error(`A saved offline action (${action.type}) failed to sync after several attempts. Please check and redo it manually.`);
                        removePendingAction(action.actionId);
                        if (action.jobId) {
                            failedJobIds.add(action.jobId);
                        }
                        const failKeyJobId = action.parentJobId || action.jobId;
                        if (action.instanceDate) {
                            if (failKeyJobId) {
                                const jobStub = { _id: failKeyJobId, parentJob: failKeyJobId };
                                const jobKey = getPendingJobKey(jobStub, action.instanceDate);
                                clearPendingUpdate(jobKey);
                            }
                        } else if (failKeyJobId) {
                            clearPendingUpdatesForJob(failKeyJobId);
                        }
                        failCount++;
                        continue; // Move on to the next action instead of breaking the whole batch
                    }
                    // Still under the cap — keep the action and try again later.
                    // We'll break the loop to avoid hammering the server.
                    toast.warn("Connection issue during sync. Will try again later.", { autoClose: 2000 });
                    stoppedEarly = true;
                    break;
                }
            }
        } // End of for...of loop

        // ⭐ NEW: Offline photo uploads. Run after the actions above, sent through
        // axios so every attempt gets a fresh security token (the old service
        // worker replay reused the token from when the photo was taken, and a
        // stale one got the photos rejected and deleted).
        // Skipped if the action loop above stopped for a connection problem,
        // in which case photos would just fail the same way.
        if (!stoppedEarly) {
            for (const entry of photosToSync) {
                try {
                    await api.post(`/jobs/${entry.jobId}/photos`, buildPhotoFormData(entry), {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    await removeQueuedPhotoUpload(entry.id);
                    successCount++;
                } catch (err) {
                    console.error(`[OfflineSyncManager] Failed to sync ${entry.photoType} photos for job ${entry.jobId}:`, err);
                    const isCsrfRejection = err.response && err.response.status === 403 && (
                        err.response.data?.code === 'EBADCSRFTOKEN' ||
                        /csrf|security token/i.test(String(err.response.data?.message || ''))
                    );
                    if (isCsrfRejection) {
                        // Keep them, try again on the next round.
                        break;
                    }
                    if (err.response && err.response.status >= 400 && err.response.status < 500) {
                        // The server refused these photos (e.g. job deleted, file
                        // type not allowed). Retrying won't help.
                        toast.error(`${entry.files.length} offline ${entry.photoType} photo(s) couldn't be uploaded: ${err.response?.data?.message || 'please add them again'}.`);
                        await removeQueuedPhotoUpload(entry.id);
                        failCount++;
                        continue;
                    }
                    if (err.response && err.response.status >= 500) {
                        // Server error: same 5-attempt cap as the actions above.
                        // A plain connection failure (no response) does NOT count
                        // towards it, so patchy signal can never use up the
                        // attempts and throw the photos away.
                        const attempts = await bumpQueuedPhotoAttempt(entry.id);
                        if (attempts !== null && attempts >= MAX_SYNC_ATTEMPTS) {
                            toast.error(`${entry.files.length} offline ${entry.photoType} photo(s) failed to upload after several attempts. Please add them again.`);
                            await removeQueuedPhotoUpload(entry.id);
                            failCount++;
                            continue;
                        }
                    }
                    // Connection problem or a retryable server error: keep the
                    // rest for the next round.
                    break;
                }
            }
        }

        if (successCount > 0) {
            toast.success(`Successfully synced ${successCount} offline change(s)! Data is refreshing.`);
            
            // Force a reload of critical data to show server-confirmed state
            // This is the correct way to refresh data without a full page reload.
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });

            // ⭐ FIX: queryClient.invalidateQueries only reaches components using
            // react-query — StaffDashboard.js's job list (rawJobs) is plain local
            // state, fetched manually via fetchAssignedJobs, and was never wired
            // up to react to anything here. That's why a successful background
            // sync used to leave the job list stuck showing pre-sync state until
            // something else (like a logout/login remount) forced a fresh fetch.
            // A window event lets any component listen and refresh itself,
            // react-query or not.
            window.dispatchEvent(new CustomEvent('offlineSyncComplete'));
        }

        if (failCount > 0) {
            toast.error(`${failCount} offline action(s) failed to sync and were removed. Please check your work.`);
        }
        
        setIsSyncing(false);

        if (getPendingActions().length > 0) {
            console.log("[OfflineSyncManager] Sync incomplete due to network error. Waiting for next attempt.");
        }

    }, [isSyncing, user]);

    useEffect(() => {
        if (isOnline) {
            // Wait a moment after coming online to ensure connection is stable
            const timer = setTimeout(syncPendingActions, 3000);
            // ⭐ FIX: also retry every 60s while online, so anything held back (e.g. a
            // CSRF rejection above) goes through without waiting for signal to drop
            // and come back. Does nothing when the queue is empty.
            // syncPendingActions returns straight away when both the action
            // queue and the offline photo queue are empty.
            const retry = setInterval(() => {
                syncPendingActions();
            }, 60000);
            return () => { clearTimeout(timer); clearInterval(retry); };
        }
    }, [isOnline, syncPendingActions]);

    return null; // This component renders nothing.
};
// ====================================================================
// ⭐ END OFFLINE SYNC MANAGER LOGIC
// ====================================================================


// ====================================================================
// ⭐ NEW: NFC Provider for reading physical tags globally
// ====================================================================
export const NfcContext = createContext(null);

export const useNfc = () => {
    const context = useContext(NfcContext);
    if (!context) {
        throw new Error("useNfc must be used within an NfcProvider");
    }
    return context;
};

export const NfcProvider = ({ children }) => {
    const [isSupported, setIsSupported] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        const checkNfcStatus = async () => {
            if (Capacitor.isNativePlatform()) {
                // ⭐ FIX: isEnabled() does not exist in @capgo/capacitor-nfc — it was crashing
                // this check on Android. The real API is getStatus(), which returns
                // 'NFC_OK' | 'NO_NFC' | 'NFC_DISABLED' | 'NDEF_PUSH_DISABLED'.
                let supported = false;
                try {
                    const supportedRes = await CapacitorNfc.isSupported();
                    supported = !!(supportedRes.isSupported || supportedRes.supported || supportedRes.value);
                    console.log('[NFC] isSupported result:', JSON.stringify(supportedRes), '-> supported =', supported);
                } catch (e) {
                    console.error('[NFC] isSupported() call failed:', e);
                }
                setIsSupported(supported);

                if (supported) {
                    try {
                        const statusRes = await CapacitorNfc.getStatus();
                        const status = statusRes?.status || statusRes;
                        console.log('[NFC] getStatus result:', JSON.stringify(statusRes));
                        setIsEnabled(status === 'NFC_OK' || status === 'NDEF_PUSH_DISABLED');
                    } catch (e) {
                        // Don't let a status probe failure disable the whole feature —
                        // assume enabled and let the actual scan surface any real problem.
                        console.warn('[NFC] getStatus() failed, assuming enabled:', e);
                        setIsEnabled(true);
                    }
                }
            }
        };
        checkNfcStatus();
    }, []);

    const scanTag = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!isSupported) {
                toast.error("NFC is not supported on this device.");
                return reject(new Error("NFC not supported"));
            }
            
            let isResolved = false;

            const setupNfc = async () => {
                try {
                    console.log('[NFC] scanTag: setting up scan session...');
                    // Ensure no ghost listeners are hanging around
                    await CapacitorNfc.removeAllListeners();

                    const handleScan = async (event) => {
                        console.log('[NFC] EVENT RECEIVED:', JSON.stringify(event));
                        if (isResolved) return;
                        isResolved = true;

                        // Stop scanning on ALL platforms
                        try { await CapacitorNfc.stopScanning(); } catch(e) {}

                        await CapacitorNfc.removeAllListeners();

                        // PASS RAW DATA DIRECTLY (Formatter in StaffJobCard handles this now)
                        resolve(event);
                    };

                    // Cover every discovery event the plugin can emit
                    await CapacitorNfc.addListener('nfcEvent', handleScan);
                    await CapacitorNfc.addListener('tagDiscovered', handleScan);
                    await CapacitorNfc.addListener('ndefDiscovered', handleScan);
                    await CapacitorNfc.addListener('ndefMimeDiscovered', handleScan);
                    await CapacitorNfc.addListener('ndefFormatableDiscovered', handleScan);

                    // ⭐ FIX: Await startScanning on EVERY platform. The old code swallowed
                    // Android errors, so a failed session start left the scan hanging forever
                    // with no visible error — the exact "button does nothing" symptom.
                    try {
                        await CapacitorNfc.startScanning();
                        console.log('[NFC] Scan session started OK — waiting for tag...');
                    } catch (startErr) {
                        console.error('[NFC] startScanning FAILED:', startErr);
                        await CapacitorNfc.removeAllListeners();
                        return reject(startErr);
                    }
                } catch (e) {
                    console.error('[NFC] Setup error:', e);
                    try { await CapacitorNfc.removeAllListeners(); } catch(e2) {}
                    reject(e);
                }
            };

            setupNfc();
        });
    }, [isSupported]);

    const cancelScan = useCallback(async () => {
        try {
            // ⭐ CRITICAL FIX: Use stopScanning() here too
            await CapacitorNfc.stopScanning();
            await CapacitorNfc.removeAllListeners();
        } catch (e) {
            console.error('[NFC] Error cancelling scan', e);
        }
    }, []);

    return (
        <NfcContext.Provider value={{ isSupported, isEnabled, scanTag, cancelScan }}>
            {children}
        </NfcContext.Provider>
    );
};
// ====================================================================


const NotFoundPage = () => {
    return (
        <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
            <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-800 dark:text-gray-200">404</h1>
                <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">| Page Not Found</p>
            </div>
        </div>
    );
};

const MapsApiProvider = ({ children }) => {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
        libraries: googleMapsLibraries,
    });

    return (
        <MapsApiContext.Provider value={{ isMapsLoaded: isLoaded, isMapsLoadError: loadError }}>
            {children}
        </MapsApiContext.Provider>
    );
};

export const useMapsApi = () => {
    const context = useContext(MapsApiContext);
    if (!context) {
        throw new Error('useMapsApi must be used within a MapsApiProvider');
    }
    return context;
};

const isSuperAdmin = (user) => {
    if (!user || !user.email) return false;
    const superAdminEmail = (process.env.REACT_APP_SUPER_ADMIN_EMAIL || '').trim().toLowerCase();
    const userEmail = user.email.trim().toLowerCase();
    return superAdminEmail && userEmail === superAdminEmail;
};

// ==========================================================
// ⭐ CRITICAL FIX: Prioritize Franchisor/Affiliate Dashboard Redirection (Used by AppContent) ⭐
// ==========================================================
const getDefaultDashboardPath = (user) => {
    if (!user || !user.role) return '/login';

    if (isSuperAdmin(user)) {
        return '/owner-dashboard';
    }
    
    // ⭐ NEW: Role-based redirection for franchisors (highest priority)
    if (user.role === 'franchisor') {
        return '/franchisors/dashboard';
    }
    
    // ⭐ FIX: Role-based redirection for pure affiliates
    if (user.role === 'affiliate') {
        return '/affiliates/dashboard';
    }

    switch (user.role) {
        case 'customer': return '/customer-portal/dashboard';
        case 'staff': return '/staff-dashboard';
        case 'manager': return '/dashboard';
        case 'admin': return '/dashboard';
        default: return '/login';
    }
};
// ==========================================================

const PrivateRoute = ({ children, roles }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    // ⭐ FIX: Wrapped Loading in Dark Mode friendly container
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300 animate-pulse">Authenticating...</p>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    // 1. Super Admin Check
    if (isSuperAdmin(user)) {
        return children;
    }
    
    // 2. ⭐ CRITICAL FRANCHISOR ENFORCEMENT FIX ⭐
    // If the user role is 'franchisor', force them to the dashboard if they try to leave.
    if (user.role === 'franchisor') {
        if (location.pathname.startsWith('/franchisors/dashboard') || location.pathname === '/franchisor-pricing') {
            return children;
        } else {
            // Allow them to visit their new pricing page
            if (location.pathname === '/franchisor-pricing') return children;
            return <Navigate to="/franchisors/dashboard" replace />;
        }
    }

    // 3. CRITICAL AFFILIATE ENFORCEMENT FIX 
    if (user.role === 'affiliate') {
        if (location.pathname.startsWith('/affiliates/dashboard')) {
            return children;
        } else {
            return <Navigate to="/affiliates/dashboard" replace />;
        }
    }
    
    // 4. Role Restriction Check (Standard Admin/Staff/Customer logic)
    if (roles && !roles.includes(user.role)) {
        const dashboardPath = getDefaultDashboardPath(user);
        // If they fail the role check (e.g., staff tries to hit /settings),
        // send them to their role-specific dashboard (staff/customer portal).
        return <Navigate to={dashboardPath} replace />;
    }

    // 5. Default Pass-through (If all checks pass and they are on a valid route for their role)
    return children;
};


// ⭐ NEW: RecaptchaWrapper component to apply the provider only to necessary routes
const RecaptchaWrapper = ({ children }) => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!recaptchaSiteKey) {
        console.error("Configuration error: reCAPTCHA site key is missing.");
        return children;
    }
    return (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
            {children}
        </GoogleReCaptchaProvider>
    );
}
// ⭐ END NEW


function AppContent() {
    const { user, loading: authLoading, logout } = useAuth();
    // ⭐ FIX: Destructure 'settings' here to check the buddy visibility setting
    const { settings, isLoading: settingsLoading } = useSettings();
    const location = useLocation();
    const navigate = useNavigate(); // ⭐ NEW: Added for redirection

    // ====================================================================
    // ⭐ NEW: NFC background tag deep-link handler.
    // A tag carries a URL like https://theone.procleaneruk.co.uk/clockin?tag=<tagId>
    // iOS/Android open the app directly at that URL (Universal Links / App Links),
    // even from fully closed, and this catches it — no manual navigation needed.
    // ====================================================================
    const [nfcJobChoice, setNfcJobChoice] = useState(null); // ⭐ NEW: { options, tagId } when a tag tap matches multiple possible jobs
    const [nfcChoiceLoading, setNfcChoiceLoading] = useState(false);
    const pendingNfcUrlRef = useRef(null); // ⭐ NEW: holds a tag-tap URL that arrived before auth finished loading on a cold start
    const lastProcessedNfcUrl = useRef(null); // ⭐ NEW: debounce guard — prevents appUrlOpen + getLaunchUrl firing the same URL twice

    // ⭐ NEW: Admin/manager users who also have a staff profile use a different route
    // (/manager-job-card) than real staff (/staff-dashboard) for the same screen — see Sidebar.js
    const getStaffDashboardPath = useCallback(() => {
        if (user?.role === 'admin' || user?.role === 'manager') return '/manager-job-card';
        return '/staff-dashboard';
    }, [user]);

    const confirmNfcJobChoice = useCallback(async (jobId) => {
        if (!nfcJobChoice) return;
        setNfcChoiceLoading(true);
        // ⭐ NEW: Capture the actual moment of this action so an offline/delayed
        // sync doesn't get stamped with whenever the request finally lands.
        const actionTimestamp = new Date().toISOString();
        try {
            let locationPayload = {};
            try {
                const coords = await Geolocation.getCurrentPosition({ timeout: 10000 });
                locationPayload = { latitude: coords.coords.latitude, longitude: coords.coords.longitude };
            } catch (geoErr) {}

            const chosenOption = nfcJobChoice.options?.find(opt => opt.jobId === jobId);
            await api.put(`/jobs/${jobId}/clock-in`, { scannedNfcTagId: nfcJobChoice.tagId, clientTimestamp: actionTimestamp, instanceDate: chosenOption?.date, ...locationPayload });
            toast.success('Clocked in');
            setNfcJobChoice(null);
            navigate(getStaffDashboardPath());
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not clock in. Please try manually.');
            setNfcJobChoice(null);
        } finally {
            setNfcChoiceLoading(false);
        }
    }, [nfcJobChoice, navigate, getStaffDashboardPath]);

    // ⭐ REMOVED: this used to POST to a temporary backend debug-logging route
    // (nfc-debug-log / nfcDebugRoutes.js), now deleted. Left as a no-op rather
    // than stripping out its ~26 call sites through the NFC deep-link flow
    // below — those calls are now harmless no-ops instead of a live 403.
    const nfcDebugLog = () => {};

    const handleNfcDeepLink = useCallback(async (incomingUrl) => {
        nfcDebugLog('handleNfcDeepLink-called', { incomingUrl });
        let parsed;
        try {
            parsed = new URL(incomingUrl);
        } catch (e) {
            return;
        }

        nfcDebugLog('parsed-url', { pathname: parsed.pathname });

        // ⭐ FIX: Password reset links. On a phone with the app installed, the
        // "Forgot password" email link opens the app (same Universal Links / App
        // Links as the NFC tags), but this handler only ever handled /clockin, so
        // the app just opened on its login screen and the reset never happened.
        // Send it to the in-app reset page with the code from the email instead.
        if (parsed.pathname.startsWith('/reset-password')) {
            navigate(`${parsed.pathname}${parsed.search}`, { replace: true });
            return;
        }

        if (!parsed.pathname.startsWith('/clockin')) return;

        const tagId = parsed.searchParams.get('tag');
        nfcDebugLog('tagId-extracted', { tagId });
        if (!tagId) return;

        // ⭐ NEW: Debounce — appUrlOpen and getLaunchUrl can both fire for the same tap
        const now = Date.now();
        if (
            lastProcessedNfcUrl.current &&
            lastProcessedNfcUrl.current.url === incomingUrl &&
            now - lastProcessedNfcUrl.current.time < 5000
        ) {
            nfcDebugLog('debounced-duplicate', { incomingUrl });
            return;
        }
        lastProcessedNfcUrl.current = { url: incomingUrl, time: now };

        // ⭐ NEW: This is the actual moment the tag was tapped — capture it now,
        // before the resolve/GPS calls (which can be slow or fail) add delay,
        // and send it through so a later offline sync keeps this real time
        // instead of getting stamped with whenever the request finally lands.
        const actionTimestamp = new Date(now).toISOString();

        // ⭐ NEW: On a true cold start (app fully closed before the tap), this can fire before
        // auth has finished checking stored credentials. Don't bail to login on a false negative —
        // queue it and a separate effect will replay it the moment auth finishes resolving.
        if (authLoading) {
            nfcDebugLog('queued-authLoading', { incomingUrl });
            pendingNfcUrlRef.current = incomingUrl;
            return;
        }

        if (!user) {
            // Genuinely not logged in (auth finished loading and there's really no user)
            nfcDebugLog('no-user-redirect-login', { incomingUrl });
            toast.error('Please log in to clock in via NFC.');
            navigate('/login');
            return;
        }

        nfcDebugLog('proceeding-to-resolve', { tagId });
        try {
            // 1. Ask the backend which job + which action (clock in or out) this tag tap means.
            // If this fails we're genuinely offline with no way to know which job — show a message and bail.
            let resolveRes;
            try {
                resolveRes = await api.get(`/jobs/resolve-by-nfc-tag/${encodeURIComponent(tagId)}`);
                nfcDebugLog('resolve-success', { data: resolveRes.data });
            } catch (resolveErr) {
                nfcDebugLog('resolve-failed', {
                    status: resolveErr.response?.status,
                    message: resolveErr.response?.data?.message,
                    isNetworkError: !resolveErr.response,
                });
                // ⭐ LIE-FI FIX: don't trust navigator.onLine blindly. If the server
                // never responded (network error) or returned 5xx, treat it as a
                // connection failure and use the offline path. A 4xx (e.g. tag not
                // found) is a real rejection and falls through to the error handling.
                const isNetworkFailure = !navigator.onLine || !resolveErr.response || resolveErr.response.status >= 500;
                if (isNetworkFailure) {
                    // ⭐ OFFLINE: Try the local NFC cache saved when jobs loaded this morning
                    try {
                        const nfcCache = JSON.parse(localStorage.getItem('nfcJobCache') || '{}');
                        const cached = nfcCache[tagId];
                        if (cached) {
                            savePendingAction({
                                type: 'clock-in',
                                jobId: cached.jobId,
                                payload: { scannedNfcTagId: tagId, clientTimestamp: actionTimestamp },
                                instanceDate: cached.instanceDate || null,
                                parentJobId: cached.parentJobId || cached.jobId
                            });
                            toast.info('Offline — clock-in saved and will sync when connected.');
                            navigate(getStaffDashboardPath());
                            return;
                        }
                    } catch (cacheErr) {
                        console.warn('NFC cache read failed:', cacheErr);
                    }
                    toast.error('You are offline. Please clock in manually when connected.');
                } else {
                    toast.error(resolveErr.response?.data?.message || 'Could not find a job for this tag. Please clock in manually.');
                }
                return;
            }
            const { action, jobId, jobDescription, date: resolvedInstanceDate } = resolveRes.data;
            nfcDebugLog('action-determined', { action, jobId });


            // 2. Location is BEST-EFFORT only — the scanned NFC tag IS the proof of
            // presence, so GPS must never block or hang the clock-in. On iOS,
            // requestPermissions() / getCurrentPosition() can stall indefinitely
            // during a cold deep-link launch, which silently killed online auto
            // clock-ins (offline worked because it never reaches this code).
            // Rules now: never prompt for permission here, and enforce a hard
            // JS-side deadline — if GPS isn't instant, proceed without it.
            let locationPayload = {};
            try {
                const permStatus = await Promise.race([
                    Geolocation.checkPermissions(),
                    new Promise((_, rej) => setTimeout(() => rej(new Error('perm check timeout')), 3000))
                ]);
                if (permStatus.location === 'granted') {
                    const coords = await Promise.race([
                        Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }),
                        new Promise((_, rej) => setTimeout(() => rej(new Error('GPS deadline exceeded')), 5000))
                    ]);
                    locationPayload = { latitude: coords.coords.latitude, longitude: coords.coords.longitude };
                    nfcDebugLog('gps-acquired', locationPayload);
                }
            } catch (geoErr) {
                nfcDebugLog('gps-skipped', { message: geoErr?.message || String(geoErr) });
                console.warn('[NFC] GPS best-effort skipped, proceeding without location:', geoErr?.message || geoErr);
                // Continue without location — backend's NFC bypass handles this
            }

            // 3. Fire the actual clock-in/out, or surface a picker if it's genuinely ambiguous
            if (action === 'clockin') {
                try {
                    await api.put(`/jobs/${jobId}/clock-in`, { scannedNfcTagId: tagId, clientTimestamp: actionTimestamp, instanceDate: resolvedInstanceDate, ...locationPayload });
                    nfcDebugLog('clockin-success', { jobId });
                    toast.success(`Clocked in${jobDescription ? ` — ${jobDescription}` : ''}`);
                } catch (clockErr) {
                    // ⭐ LIE-FI FIX: network error or 5xx = connection failure, queue it
                    const isNetworkFailure = !navigator.onLine || !clockErr.response || clockErr.response.status >= 500;
                    nfcDebugLog('clockin-failed', { status: clockErr.response?.status, isNetworkFailure, message: clockErr.response?.data?.message });
                    if (isNetworkFailure) {
                        // ⭐ OFFLINE: resolver succeeded so we know which job — queue it for sync later
                        savePendingAction({
                            type: 'clock-in',
                            jobId,
                            payload: { scannedNfcTagId: tagId, clientTimestamp: actionTimestamp, instanceDate: resolvedInstanceDate, ...locationPayload },
                        });
                        toast.info(`Offline — clock-in saved and will sync when connected.`);
                    } else {
                        toast.error(clockErr.response?.data?.message || 'Could not clock in. Please try manually.');
                        return;
                    }
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate(getStaffDashboardPath());
            } else if (action === 'clockout') {
                try {
                    await api.put(`/jobs/${jobId}/clock-out`, { scannedNfcTagId: tagId, clientTimestamp: actionTimestamp, instanceDate: resolvedInstanceDate, ...locationPayload });
                    nfcDebugLog('clockout-success', { jobId });
                    toast.success(`Clocked out${jobDescription ? ` — ${jobDescription}` : ''}`);
                } catch (clockErr) {
                    // ⭐ LIE-FI FIX: network error or 5xx = connection failure, queue it
                    const isNetworkFailure = !navigator.onLine || !clockErr.response || clockErr.response.status >= 500;
                    nfcDebugLog('clockout-failed', { status: clockErr.response?.status, isNetworkFailure, message: clockErr.response?.data?.message });
                    if (isNetworkFailure) {
                        // ⭐ OFFLINE: resolver succeeded so we know which job — queue it for sync later
                        savePendingAction({
                            type: 'clock-out',
                            jobId,
                            payload: { scannedNfcTagId: tagId, clientTimestamp: actionTimestamp, instanceDate: resolvedInstanceDate, ...locationPayload },
                        });
                        toast.info(`Offline — clock-out saved and will sync when connected.`);
                    } else {
                        toast.error(clockErr.response?.data?.message || 'Could not clock out. Please try manually.');
                        return;
                    }
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate(getStaffDashboardPath());
            } else if (action === 'choose') {
                // More than one job at this address today — let the staff member pick which one
                nfcDebugLog('multiple-jobs-choice-shown', { options: resolveRes.data.options });
                setNfcJobChoice({ options: resolveRes.data.options, tagId });
            }
        } catch (err) {
            nfcDebugLog('outer-catch-error', { message: err.response?.data?.message || err.message || String(err) });
            toast.error(err.response?.data?.message || 'Could not process NFC clock-in/out. Please open the app and clock in manually.');
            navigate(getStaffDashboardPath());
        }
    }, [user, authLoading, navigate, getStaffDashboardPath]);

    // ⭐ NEW: Once auth finishes loading, replay any tag tap that arrived too early on a cold start
    useEffect(() => {
        nfcDebugLog('replay-effect-fired', { authLoading, hasQueuedUrl: !!pendingNfcUrlRef.current });
        if (!authLoading && pendingNfcUrlRef.current) {
            const queuedUrl = pendingNfcUrlRef.current;
            pendingNfcUrlRef.current = null;
            nfcDebugLog('replay-effect-replaying', { queuedUrl });
            handleNfcDeepLink(queuedUrl);
        }
    }, [authLoading, handleNfcDeepLink]);

    // ⭐ NEW: One-time launch-URL check. This must only ever run once, on true
    // app mount — NOT every time handleNfcDeepLink's identity changes (e.g. on
    // background resume, when `user` gets a new object reference). getLaunchUrl()
    // reflects whatever URL originally started the app process; re-checking it
    // on every resume was replaying that same stale tap over and over, and the
    // repeated effect teardown/re-setup was also interrupting in-flight taps
    // partway through (no resolve call ever firing) — the inconsistent behaviour
    // reported tonight. Empty dependency array ensures this runs exactly once.
    useEffect(() => {
        const checkLaunchUrl = async () => {
            if (!Capacitor.isNativePlatform()) return; // Universal/App Links only apply to the native app

            nfcDebugLog('js-listener-setup-start', { platform: Capacitor.getPlatform() });

            // Catches the tap when the app was fully closed and just launched because of it
            const launchResult = await CapacitorApp.getLaunchUrl();
            nfcDebugLog('getLaunchUrl-result', { launchResult });
            if (launchResult?.url) {
                nfcDebugLog('using-getLaunchUrl', { url: launchResult.url });
                handleNfcDeepLink(launchResult.url);
            } else {
                // ⭐ FIX: iOS cold-start gap — getLaunchUrl() often misses the tap because
                // the WebView isn't ready when iOS delivers the Universal Link. AppDelegate
                // now stashes it natively (see continue userActivity); pick it up here instead.
                try {
                    const stored = await Preferences.get({ key: 'pendingNfcLaunchUrl' });
                    nfcDebugLog('preferences-check-result', { stored });
                    if (stored?.value) {
                        await Preferences.remove({ key: 'pendingNfcLaunchUrl' });
                        nfcDebugLog('using-preferences-fallback', { url: stored.value });
                        handleNfcDeepLink(stored.value);
                    }
                } catch (e) {
                    nfcDebugLog('preferences-fallback-error', { message: String(e) });
                    console.warn('[NFC] Preferences fallback read failed:', e);
                }
            }
        };

        checkLaunchUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ⭐ NEW: Live tap listener — separate from the one-time launch-URL check above.
    // This still needs to react to handleNfcDeepLink changing (so the closure
    // stays current), but no longer re-triggers getLaunchUrl on every re-run.
    useEffect(() => {
        // ⭐ FIX: closes a real race — if this effect re-runs (e.g. `user`
        // getting a new object reference on a background refetch) while the
        // previous run's `await CapacitorApp.addListener(...)` hasn't
        // resolved yet, cleanup used to fire with `listenerHandle` still
        // undefined, so `listenerHandle?.remove()` silently did nothing —
        // leaving the OLD listener alive natively. A moment later the old
        // addListener call finally resolved and registered anyway, leaving
        // TWO live listeners both wired to the same physical tag. `cancelled`
        // makes sure that if this effect tears down before the listener
        // finished registering, we remove it the moment it does resolve
        // instead of leaving it dangling. Pure safety addition — if this
        // race never gets hit (as on iOS, which already works fine),
        // behavior is unchanged either way.
        let cancelled = false;
        let listenerHandle;

        const setupAppUrlOpenListener = async () => {
            if (!Capacitor.isNativePlatform()) return;

            // Catches the tap while the app is already running or backgrounded
            const handle = await CapacitorApp.addListener('appUrlOpen', (event) => {
                nfcDebugLog('appUrlOpen-fired', { url: event.url });
                handleNfcDeepLink(event.url);
            });

            if (cancelled) {
                handle.remove(); // effect already torn down — don't leak this listener
                return;
            }
            listenerHandle = handle;
        };

        setupAppUrlOpenListener();

        return () => {
            cancelled = true;
            listenerHandle?.remove();
        };
    }, [handleNfcDeepLink]);
    // ====================================================================
    // ⭐ END NEW: NFC background tag deep-link handler
    // ====================================================================

    // ====================================================================
    // ⭐ NEW: Add the push registration lock to prevent duplicate API calls
    // ====================================================================
    const pushRegistrationLock = useRef(false);

    // ⭐ NEW: Reset the lock if the user logs out and logs back in
    useEffect(() => {
        pushRegistrationLock.current = false;
    }, [user?.firebaseUid]);
    // ====================================================================

    // ====================================================================
    // ⭐ NEW: Status Bar Initialisation for iOS/Android
    // ====================================================================
    useEffect(() => {
        const setupStatusBar = async () => {
            // Only run this on native mobile platforms, not the web
            if (Capacitor.isNativePlatform()) {
                try {
                    // Force the style to light (dark text)
                    await StatusBar.setStyle({ style: Style.Light });
                    
                    // Force the webview to sit below the status bar, not under it
                    await StatusBar.setOverlaysWebView({ overlay: false });
                    
                    // Set the background colour to match your header
                    await StatusBar.setBackgroundColor({ color: '#ffffff' });
                } catch (error) {
                    console.error('Error setting up status bar:', error);
                }
            }
        };

        setupStatusBar();
    }, []);
    // ====================================================================

    const [customers, setCustomers] = useState([]);
    const [staff, setStaff] = useState([]);
    const [jobTypes, setJobTypes] = useState([]); // ⭐ NEW: State for job types
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [loadingStaff, setLoadingStaff] = useState(true);
    
    const [isFirebaseLoaded, setIsFirebaseLoaded] = useState(false);
    
    // ⭐ NEW: State for Booking Buddy Modal
    const [isBookingBuddyOpen, setIsBookingBuddyOpen] = useState(false);
    const toggleBookingBuddy = () => setIsBookingBuddyOpen(!isBookingBuddyOpen);

    const isLandingPage = location.pathname === '/';
    const isPlumbersPage = location.pathname === '/plumbers';
    const isElectriciansPage = location.pathname === '/electricians';
    const isLandscapingPage = location.pathname === '/landscaping';
    const isHvacPage = location.pathname === '/hvac';
    const isPmPage = location.pathname === '/propertymanagement';
    const isSecurityPage = location.pathname === '/security'; // <--- NEW SECURITY check
    
    const isAuthPage = ['/login', '/signup', '/forgot-password', '/verify-2fa', '/pricing', '/subscription-success', '/franchisor-pricing'].includes(location.pathname);
    // ⭐ NEW CHECK: Affiliate Sign Up Page
    const isAffiliateSignUpPage = location.pathname.startsWith('/affiliate/signup');
    // ⭐ NEW CHECK: Franchisor Sign Up Page
    const isFranchisorSignUpPage = location.pathname.startsWith('/franchisor/signup');

    const isFormPage = location.pathname.startsWith('/forms/');
    const isPublicReviewFormPage = location.pathname.startsWith('/public/forms/');
    const isPublicQuotePage = location.pathname.startsWith('/public/quotes/');
    // ⭐ NEW CHECK: Public Job Photos Page
    const isPublicJobPhotosPage = location.pathname.startsWith('/public/photos/');
    // ⭐ NEW CHECK: Public Scratch Card Page
    const isPublicScratchCardPage = location.pathname.startsWith('/public/scratch-card/');
    // ⭐ NEW CHECK: Public Application Page
    const isPublicApplicationPage = location.pathname.startsWith('/apply/');
    // ⭐ NEW CHECK: Public Careers Page
    const isPublicCareersPage = location.pathname.startsWith('/careers/');
    // ⭐ NEW CHECK: Public Assessment Page
    const isPublicAssessmentPage = location.pathname.startsWith('/public-assessment/');

    const isChatbotEmbedPage = location.pathname.startsWith('/chatbot/'); 
    // ⭐ NEW CHECK: Landing Bot Embed Page ⭐
    const isLandingBotEmbedPage = location.pathname.startsWith('/landing-embed/');
    // ⭐ NEW CHECK: Public Smart Booking Page ⭐
    const isPublicSmartBookingPage = location.pathname.startsWith('/public/booking/');
    // ⭐ NEW CHECK: Public Book Online Page ⭐
    const isPublicBookOnlinePage = location.pathname.startsWith('/public/book/');
    // ⭐ NEW CHECK: Public Quote Form (Quote Forms) ⭐
    const isPublicQuoteFormPage = location.pathname.startsWith('/public/quote-form/');
    // ⭐ NEW: Landing Pages (/page/:slug). Not called isLandingPage, which is the homepage check above.
    const isPublicLandingPageRoute = location.pathname.startsWith('/page/');
    
    // ⭐ NEW CHECK: Mobile App Download Page ⭐
    const isDownloadPage = location.pathname === '/download-app';
    
    // ⭐ NEW CHECK: Public Directory Page ⭐
    const isPublicDirectoryPage = location.pathname.startsWith('/directory/');

    // * ⭐ CRITICAL ADDITION TO HANDLE /reset-password *
    const isResetPasswordPage = location.pathname.startsWith('/reset-password');
    // ⭐ MODIFIED: Include the new public page checks
    const isPublicPage = isAuthPage || isFormPage || isPublicReviewFormPage || isPublicQuotePage || isPublicJobPhotosPage || isPublicScratchCardPage || isPublicApplicationPage || isPublicCareersPage || isPublicAssessmentPage || isResetPasswordPage || isChatbotEmbedPage || isLandingBotEmbedPage || isPublicSmartBookingPage || isDownloadPage || isPublicDirectoryPage || (!user && isLandingPage) || isPlumbersPage || isElectriciansPage || isLandscapingPage || isPublicSmartBookingPage || isHvacPage || isPmPage || isSecurityPage || isAffiliateSignUpPage || isFranchisorSignUpPage || isPublicBookOnlinePage || isPublicQuoteFormPage || isPublicLandingPageRoute;
    // * ⭐ END OF CRITICAL ADDITION *


    const noSidebarPaths = [
        '/staff-dashboard',
        '/staff-portal/schedule', 
        '/staff-portal/my-payslips', 
        '/staff-portal/my-hours', // ⭐ NEW: No sidebar for the hours review page
        '/staff-portal/my-attendance', // ⭐ NEW: No sidebar for the daily attendance review page
        '/staff-portal/my-expenses', 
        '/staff-portal/my-absences', 
        '/staff-portal/contracts', 
        '/staff-portal/contracts/',
        '/staff-portal/my-invoices', // ⭐ NEW: No sidebar for the staff invoices page 
        '/staff/invoices/', // ⭐ NEW: No sidebar for staff invoice details page
        '/staff-portal/my-reviews', // ⭐ NEW: No sidebar for the staff reviews page
        '/staff-portal/my-rewards', // ⭐ NEW: No sidebar for the staff rewards page
        // ⭐ NEW ADDITIONS FOR STAFF TRAINING MODULE ⭐
        '/staff-portal/training', 
        '/staff-portal/training/',
        // ⭐ END NEW ADDITIONS ⭐
        '/customer-portal/dashboard',
        '/customer-portal/invoices',
        '/customer-portal/appointments',
        '/customer-portal/emergency',
        '/customer-portal/job-history',
        '/customer-portal/invoices/',
        '/customer-portal/request-quote',
        '/customer-portal/quotes',
        '/customer-portal/quotes/',
        '/customer-portal/quote-requests/',
        '/customer-portal/contracts',
        '/customer-portal/contracts/',
        // ⭐ NEW: Loyalty Page needs no sidebar ⭐
        '/customer-portal/loyalty',
        '/staff-portal', // <-- Existing base path
        '/admin/quotes/create-from-lead/',
        '/admin/quotes/create-from-request/',
        '/admin-chat',
        '/staff-portal/chat', // <-- ADDED: Specific path for chat
        '/customer-portal/chat',
        '/public/forms/',
        '/public/quotes/',
        // ⭐ NEW ADDITION: Public Photo Page
        '/public/photos/',
        // ⭐ NEW ADDITION: Public Scratch Card Page
        '/public/scratch-card/',
        // ⭐ NEW ADDITION: Public Application Page
        '/apply/',
        // ⭐ NEW ADDITION: Public Careers Page
        '/careers/',
        // ⭐ NEW ADDITION: Public Assessment Page
        '/public-assessment/',
        // ⭐ NEW ADDITION: Public Smart Booking Page
        '/public/booking/',
        // ⭐ NEW ADDITION: Public Quote Form (Quote Forms)
        '/public/quote-form/',
        // ⭐ NEW ADDITION: Public Landing Page (Landing Pages)
        '/page/',
        
        // ⭐ NEW ADDITION: Payment Redirect (no sidebar) ⭐
        '/pay/',
        
        '/manager-job-card',
        '/landing-page-builder',
        
        '/owner-dashboard',
        '/pricing',
        '/subscription-success',
        // * ⭐ Explicitly ensure reset page has no sidebar *
        '/reset-password',
        // * ⭐ END OF EXPLICIT ADDITION *
        '/chatbot/',
        // ⭐⭐⭐ NEW ADDITION: Landing Bot Embed Page (transparent background) ⭐⭐⭐
        '/landing-embed/',
        // * ⭐ NEW ADDITION: Staff Quote Calculator (should have no sidebar) *
        '/staff-portal/quote-calculator',
        // * ⭐ END OF NEW ADDITION *
        
        // ⭐ NEW ADDITION: Download Page (no sidebar) ⭐
        '/download-app',

        // ==========================================================
        // ⭐ NEW ADDITION: Affiliate and Franchisor Dashboards (Full Width) ⭐
        '/affiliates/dashboard',
        '/franchisors/dashboard',
        // ⭐ NEW ADDITION: Franchisor Pricing Page (Full Width) ⭐
        '/franchisor-pricing',
        // ==========================================================
        // ⭐ NEW ADDITION: Directory Pages ⭐
        '/directory/',
    ];
    const isNoSidebarPage = noSidebarPaths.some(path => location.pathname.startsWith(path));

    // The Sidebar should be rendered if the user is logged in AND they are NOT a franchisor/affiliate
    const shouldRenderSidebarLayout = user && !authLoading && !isPublicPage && !isNoSidebarPage && !['affiliate', 'franchisor'].includes(user.role);
    
    // ⭐ NEW: Check if user is Admin or Manager AND if Buddy is enabled in settings
    // This fixed the issue where the bubble was visible even when disabled.
    const isBookingBuddyEnabled = settings?.chatbotSettings?.buddyChatVisible;
    const isBookingUser = user && 
                          ['admin', 'manager'].includes(user.role) && 
                          shouldRenderSidebarLayout && 
                          isBookingBuddyEnabled; 

    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

    const registerForPushNotifications = useCallback(async () => {
        // ====================================================================
        // ⭐ NEW: Abort if we are already registering or have already registered
        // ====================================================================
        if (pushRegistrationLock.current) {
            console.log('App.js: Push registration already handled. Skipping duplicate.');
            return;
        }
        pushRegistrationLock.current = true; // Lock the gates
        // ====================================================================


        // 1. NATIVE LOGIC (iOS/Android)
        if (Capacitor.getPlatform() !== 'web') {
            try {
                // Clear old listeners to prevent duplicates
                await PushNotifications.removeAllListeners();

                // Listen for the token
                await PushNotifications.addListener('registration', async (token) => {
                    console.log('Initial Native Token (APNs on iOS, FCM on Android):', token.value);
                    
                    // 🚨 THE INVISIBLE PAUSE: This replaces the 'STEP 1' alert box!
                    // It forces the app to wait for 2 seconds (mimicking you pressing 'OK') 
                    // to give Apple and Firebase time to finish their background handshake.
                    await new Promise(resolve => setTimeout(resolve, 2000)); 

                    try {
                        // 🚨 CRITICAL FIX: Start this as null so iOS doesn't accidentally send the Apple APNs token
                        let finalFcmToken = null; 
                        
                        // If on iOS, we must swap the APNs token for an FCM token
                        if (Capacitor.getPlatform() === 'ios') {
                            console.log('Waiting for Firebase to generate FCM token...');
            
                            let attempts = 0;
                            const maxAttempts = 10; // Try for up to 5 seconds
                            let lastFirebaseError = "No error recorded"; // 🚨 Captures the real error
            
                            while (attempts < maxAttempts) {
                                try {
                                    const fcmResult = await FCM.getToken();
                                    // If we successfully get a string back, the conversion is done
                                    if (fcmResult && fcmResult.token) {
                                        finalFcmToken = fcmResult.token;
                                        console.log('Converted iOS FCM Token:', finalFcmToken);
                                        break; // Success, break out of the loop
                                    }
                                } catch (err) {
                                    // 🚨 Record the actual error instead of ignoring it
                                    lastFirebaseError = err.message || JSON.stringify(err);
                                    console.log(`Token not ready yet (Attempt ${attempts + 1}/${maxAttempts}). Error: ${lastFirebaseError}`);
                                }
                                
                                attempts++;
                                // Wait 500ms before checking again
                                await new Promise(resolve => setTimeout(resolve, 500));
                            }
            
                            if (!finalFcmToken) {
                                console.error('Failed to get FCM token from Google after 5 seconds. Error:', lastFirebaseError);
                                return; // Exit early if it completely fails to avoid sending null to your backend
                            }
                        } else {
                            // 🚨 CRITICAL FIX: Android natively returns the FCM token right away
                            finalFcmToken = token.value;
                        }

                        // Send the correct FCM token to your backend
                        if (user && user.firebaseUid && finalFcmToken) {
                            await api.post('/auth/register-fcm-token', {
                                fcmToken: finalFcmToken,
                                firebaseUid: user.firebaseUid,
                                platform: Capacitor.getPlatform()
                            });
                            console.log('FCM token sent to backend successfully.');
                        }
                    } catch (err) {
                        console.error('Error fetching or sending FCM token:', err);
                    }
                });

                // Listen for errors
                await PushNotifications.addListener('registrationError', (err) => {
                    console.error('Apple/Google Registration Error: ', err.error);
                });

                // Request permission & Register
                let permStatus = await PushNotifications.requestPermissions();
                if (permStatus.receive === 'granted') {
                    await PushNotifications.register();
                } else {
                    console.error('User denied push notification permission.');
                }

                // Stop here so we don't run Web logic on the phone
                return; 
            } catch (error) {
                console.error('Native Setup Error: ', error);
                return;
            }
        }

        // 2. PWA/WEB LOGIC
        // (This runs only if platform is 'web')
        const { messagingPromise } = await initializeFirebase();
        const messaging = await messagingPromise;

        if (!messaging) return; // Not supported
        if (!('Notification' in window)) return; // No desktop notifications
        if (!user || !user.firebaseUid) return; // Not logged in

        try {
            if (Notification.permission === 'denied') {
                console.log('User has blocked notifications.');
                return;
            }

            if (Notification.permission === 'default') {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return;
            }

            const swRegistration = await navigator.serviceWorker.ready;
            const currentToken = await messaging.getToken({ 
                serviceWorkerRegistration: swRegistration,
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY 
            });

            if (currentToken) {
                await api.post('/auth/register-fcm-token', {
                    fcmToken: currentToken,
                    firebaseUid: user.firebaseUid,
                    platform: 'web' // ⭐ ADDED PLATFORM FLAG
                });
            }
        } catch (error) {
            console.error('Error getting web FCM token:', error);
        }
    }, [user]);
    
    useEffect(() => {
        const loadFirebase = async () => {
            try {
                await initializeFirebase();
                setIsFirebaseLoaded(true);
            } catch (error) {
                console.error("Failed to load Firebase in AppContent:", error);
            }
        };

        if (!isFirebaseLoaded) {
            loadFirebase();
        }
    }, [isFirebaseLoaded]);

    useEffect(() => {
        const fetchCustomersAndStaff = async () => {
            // ⭐ MODIFICATION START
            if (authLoading || settingsLoading || !user) {
                setCustomers([]);
                setStaff([]);
                setJobTypes([]);
                setLoadingCustomers(false);
                setLoadingStaff(false);
                return;
            }
    
            // Only fetch customers and staff if they are an operational user (Admin/Manager/Staff)
            if (['admin', 'manager', 'staff'].includes(user.role)) {
                try {
                    // Only fetch customers and staff from API
                    const [resCustomers, resStaff] = await Promise.all([
                        api.get('/customers'),
                        api.get('/staff'),
                    ]);

                    setCustomers(resCustomers.data);
                    setStaff(resStaff.data);
                    
                    // ⭐ FIX: Extract job types from the `settings` state provided by the useSettings hook
                    const types = settings?.jobTypes || [];
                    setJobTypes(types);

                } catch (err) {
                    console.error("Failed to fetch customers/staff/settings in AppContent:", err);
                } finally {
                    setLoadingCustomers(false);
                    setLoadingStaff(false);
                }
            } else {
                // If franchisor or affiliate, we don't need to fetch standard operational data
                setCustomers([]);
                setStaff([]);
                setJobTypes([]);
                setLoadingCustomers(false);
                setLoadingStaff(false);
            }
            // ⭐ MODIFICATION END
        };
    
        const setupPushNotifications = () => {
            if (!user || !isFirebaseLoaded) return;

            // IF NATIVE: Run immediately (the function handles the checks)
            if (Capacitor.getPlatform() !== 'web') {
                registerForPushNotifications();
                return;
            }

            // IF WEB: Check for browser support first
            if ('Notification' in window) {
                if (Notification.permission === 'granted' || Notification.permission === 'default') {
                    registerForPushNotifications();
                } else {
                    console.log("Push Notifications: Permission previously denied.");
                }
            }
        };

        const handleSWMessage = (event) => {
            if (event.data && event.data.type === 'SW_ACTIVATED') {
                console.log("App.js: Received SW_ACTIVATED message. Setting up push notifications.");
                setupPushNotifications();
            }
        };

        window.addEventListener('message', handleSWMessage);
        
        // * MODIFICATION START: Native vs Web Push Check *
        if (Capacitor.getPlatform() !== 'web') {
            // 1. If we are on a native app (iOS/Android), fire immediately
            console.log("App.js: Native platform detected. Setting up push notifications.");
            setupPushNotifications();
        } else if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            // 2. If we are on the web, wait for the service worker
            console.log("App.js: Service worker is active. Setting up web push notifications.");
            setupPushNotifications();
        }
        // * MODIFICATION END *
    
        if (isFirebaseLoaded && !authLoading && !settingsLoading) {
            fetchCustomersAndStaff();
        }
    
        let unsubscribeOnMessage;

        const setupMessagingListeners = async () => {
            const { messagingPromise } = await initializeFirebase();
            const messaging = await messagingPromise;
            
            if (messaging) {
                unsubscribeOnMessage = messaging.onMessage((payload) => {
                    console.log('Foreground message received:', payload);
                });

                messaging.onTokenRefresh = async (refreshedToken) => {
                    console.log('FCM Token has been refreshed. Updating on server...');
                    try {
                        if (user && user.firebaseUid && refreshedToken) {
                            await api.post('/auth/register-fcm-token', {
                                fcmToken: refreshedToken,
                                firebaseUid: user.firebaseUid,
                                platform: 'web' // ⭐ ADDED PLATFORM FLAG
                            });
                            console.log('Refreshed FCM token sent to backend successfully.');
                        } else {
                            console.log('User not logged in or token missing, skipping refresh update.');
                        }
                    } catch (error) {
                        console.error('Error sending refreshed FCM token to backend:', error);
                    }
                };
            }
        };
    
        if (isFirebaseLoaded && user) {
            setupMessagingListeners();
        }
    
        return () => {
            if (unsubscribeOnMessage) unsubscribeOnMessage();
            window.removeEventListener('message', handleSWMessage);
        };
    
        // ⭐ FIX: Added 'settings' to the dependency array
    }, [user, authLoading, settingsLoading, isFirebaseLoaded, registerForPushNotifications, settings]);


    useLayoutEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        if (shouldRenderSidebarLayout) {
            handleResize();
            window.addEventListener('resize', handleResize);
        }

        return () => {
            if (shouldRenderSidebarLayout) {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, [shouldRenderSidebarLayout]);

    // ⭐ FIX: the Mac zoom is for the app's own screens only. Pages the public sees (customers,
    // job applicants, embedded widgets) always show at full size: zoom shrank them for every Mac
    // visitor, and Safari doesn't line the mouse up with zoomed pages (the Landing Page slider
    // handle sat well to the side of the pointer). Login, sign-up and ProCleanerUK's own
    // marketing pages keep the zoom as before.
    const isCustomerFacingPage = isPublicLandingPageRoute || isPublicQuoteFormPage
        || isFormPage || isPublicReviewFormPage || isPublicQuotePage || isPublicJobPhotosPage
        || isPublicScratchCardPage || isPublicSmartBookingPage || isPublicBookOnlinePage
        || isPublicApplicationPage || isPublicCareersPage || isPublicAssessmentPage
        || isPublicDirectoryPage || isChatbotEmbedPage || isLandingBotEmbedPage;
    useLayoutEffect(() => {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

        if (isMac && !isCustomerFacingPage) {
            const savedZoom = localStorage.getItem('appZoomLevel');
            if (savedZoom) {
                document.documentElement.style.zoom = savedZoom;
            } else {
                const defaultZoom = '0.8'; 
                document.documentElement.style.zoom = defaultZoom;
                localStorage.setItem('appZoomLevel', defaultZoom);
            }
        } else if (isMac) {
            document.documentElement.style.zoom = '1';
        }
        
        return () => {
            document.documentElement.style.zoom = '1';
        };
    }, [isCustomerFacingPage]);


    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const mainContentMarginClass = shouldRenderSidebarLayout
        ? (isSidebarOpen ? 'md:ml-64' : 'md:ml-20')
        : 'ml-0';

    const mobileHeaderHeight = 'calc(4rem + env(safe-area-inset-top, 0px))';

    // ⭐ FIX: Wrapped Loading Data in Dark Mode friendly container
    if (authLoading || settingsLoading || loadingCustomers || loadingStaff || !isFirebaseLoaded) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300 animate-pulse">Loading application data...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
            {/* ⭐ NEW: NFC tag tap matched more than one job — pick which one, no guessing */}
            {nfcJobChoice && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-5">
                        <h2 className="text-lg font-bold mb-1 text-gray-800 dark:text-gray-100">Which job?</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">You have more than one job here today — pick the one you're clocking in for.</p>
                        <div className="space-y-2">
                            {nfcJobChoice.options.map(opt => (
                                <button
                                    key={opt.jobId}
                                    disabled={nfcChoiceLoading}
                                    onClick={() => confirmNfcJobChoice(opt.jobId)}
                                    className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                                >
                                    <div className="font-semibold text-gray-800 dark:text-gray-100">{opt.jobDescription || 'Job'}</div>
                                    {opt.time && <div className="text-sm text-gray-500 dark:text-gray-400">{opt.time}</div>}
                                </button>
                            ))}
                        </div>
                        <button
                            disabled={nfcChoiceLoading}
                            onClick={() => setNfcJobChoice(null)}
                            className="mt-4 w-full text-center px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ⭐ OFFLINE SYNC: Add the Sync Manager and Offline Banner */}
            <OfflineBanner />
            <OfflineSyncManager /> 
            
            {/* ⭐ NEW: REGION REDIRECT MODAL ⭐ */}
            {/* This checks user location vs domain and prompts a switch if needed */}
            {!isChatbotEmbedPage && !isLandingBotEmbedPage && !isPublicPage && (
                <RegionRedirectModal />
            )}

            <ServiceWorkerWrapper />

            {shouldRenderSidebarLayout && (
                <header 
                    className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 shadow-md md:hidden fixed top-0 left-0 right-0 z-50 h-16"
                    style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(4rem + env(safe-area-inset-top, 0px))' }}
                >
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white">The One</h1>
                    <button onClick={toggleSidebar} className="p-2 text-gray-600 dark:text-gray-300 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" aria-label="Open sidebar">
                        <Bars3Icon className="h-6 w-6" />
                    </button>
                </header>
            )}

            <div className="flex flex-1">
                <DndProvider backend={HTML5Backend}>
                    {shouldRenderSidebarLayout && (
                        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} user={user} logout={logout} />
                    )}

                    <main className={`flex-1 bg-gray-100 dark:bg-gray-900 transition-all duration-300 ease-in-out ${mainContentMarginClass} ${isPublicPage ? '' : 'overflow-auto'}`}
                          style={{ paddingTop: shouldRenderSidebarLayout && window.innerWidth < 768 ? mobileHeaderHeight : '1px' }}
                    >
                        {/* ⭐ FIX: Wrapped Router Suspense in Dark Mode friendly container */}
                        <Suspense fallback={
                            <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-100 dark:bg-gray-900">
                                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                            </div>
                        }>
                            <Routes>
                                 {/* Public Routes - NOW WRAPPED WITH RECAPTCHA ONLY WHERE NEEDED */}
                                 <Route path="/login" element={<RecaptchaWrapper><LoginPage /></RecaptchaWrapper>} />
                                <Route path="/signup" element={<RecaptchaWrapper><SignUpPage /></RecaptchaWrapper>} />

                                {/* ⭐ ADD THE NEW PAYMENT REDIRECT ROUTE HERE ⭐ */}
                                <Route path="/pay/:jobId" element={<PaymentRedirect />} />
                                
                                {/* ⭐ EXISTING AFFILIATE SIGNUP ROUTE ⭐ */}
                                <Route path="/affiliate/signup" element={<RecaptchaWrapper><AffiliateSignUpPage /></RecaptchaWrapper>} />
                                {/* ⭐ NEW FRANCHISOR SIGNUP ROUTE ⭐ */}
                                <Route path="/franchisor/signup" element={<RecaptchaWrapper><FranchisorSignUpPage /></RecaptchaWrapper>} />
                                
                                <Route path="/forgot-password" element={<RecaptchaWrapper><ForgotPasswordPage /></RecaptchaWrapper>} />
                                <Route path="/verify-2fa" element={<RecaptchaWrapper><Verify2FA /></RecaptchaWrapper>} />

                                {/* ⭐⭐ ALL PUBLIC FORMS NOW WRAPPED AS REQUESTED ⭐⭐ */}
                                <Route path="/forms/:id" element={<RecaptchaWrapper><PublicFormPage /></RecaptchaWrapper>} />
                                <Route path="/public/forms/:formId/submit" element={<RecaptchaWrapper><PublicReviewFormPage /></RecaptchaWrapper>} />
                                <Route path="/public/quotes/:token" element={<RecaptchaWrapper><PublicQuotePage /></RecaptchaWrapper>} />
                                
                                {/* * ⭐ NEW PUBLIC ROUTE FOR JOB PHOTOS * */}
                                <Route path="/public/photos/:token" element={<RecaptchaWrapper><PublicJobPhotosPage /></RecaptchaWrapper>} />
                                
                                {/* * ⭐ NEW PUBLIC ROUTE FOR SCRATCH CARD GAME * */}
                                <Route path="/public/scratch-card/:slug" element={<RecaptchaWrapper><PublicScratchCardPage /></RecaptchaWrapper>} />

                                {/* * ⭐ NEW PUBLIC ROUTE FOR SMART BOOKING PAGE * */}
                                <Route path="/public/booking/:companyId" element={<RecaptchaWrapper><PublicSmartBookingPage /></RecaptchaWrapper>} />
                                {/* ⭐ NEW: Generic Book Online page */}
                                <Route path="/public/book/:companyId" element={<RecaptchaWrapper><BookOnlinePage /></RecaptchaWrapper>} />
                                {/* ⭐ NEW: Quote Forms public form (add ?embed=1 for the website embed) */}
                                <Route path="/public/quote-form/:companyId/:slug" element={<PublicQuoteFormPage />} />
                                <Route path="/page/:slug" element={<PublicLandingPage />} /> {/* ⭐ NEW: Landing Pages */}
                                
                                {/* * ⭐ NEW PUBLIC ROUTE FOR CAREERS HUB * */}
                                <Route path="/careers/:companyId" element={<CareersHub />} />

                                {/* * ⭐ NEW PUBLIC ROUTE FOR JOB APPLICATIONS * */}
                                <Route path="/apply/:companyId" element={<RecaptchaWrapper><JobApplicationForm /></RecaptchaWrapper>} />
                                <Route path="/apply/:companyId/:jobAdvertId" element={<RecaptchaWrapper><JobApplicationForm /></RecaptchaWrapper>} />

                                {/* * ⭐ NEW PUBLIC ROUTE FOR ASSESSMENTS * */}
                                <Route path="/public-assessment/:applicantId/:moduleId" element={<PublicAssessmentView />} />

                                {/* * ⭐ NEW PUBLIC ROUTE FOR MOBILE APP DOWNLOAD * */}
                                <Route path="/download-app" element={<RecaptchaWrapper><DownloadAppPage /></RecaptchaWrapper>} />
                                
                                {/* * ⭐ CRITICAL FIX ROUTE FOR PASSWORD RESET * */}
                                <Route path="/reset-password" element={<RecaptchaWrapper><ResetPasswordPage /></RecaptchaWrapper>} />
                                
                                <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />

                                {/* Chatbot embed routes */}
                                <Route path="/chatbot/:companyId" element={<EmbeddedChatbotPage />} />
                                {/* ⭐⭐⭐ NEW ROUTE: Landing Bot Embed Page ⭐⭐⭐ */}
                                <Route path="/landing-embed/:companyId" element={<EmbeddedLandingBotPage />} />
                                
                                {/* Customer Portal Routes */}
                                <Route path="/customer-portal/dashboard" element={<PrivateRoute roles={['customer']}><CustomerDashboard /></PrivateRoute>} />
                                <Route path="/customer-portal/invoices" element={<PrivateRoute roles={['customer']}><CustomerInvoicesPage /></PrivateRoute>} />
                                <Route path="/customer-portal/appointments" element={<PrivateRoute roles={['customer']}><CustomerAppointmentsPage /></PrivateRoute>} />
                                <Route path="/customer-portal/emergency" element={<PrivateRoute roles={['customer']}><CustomerEmergencyPage /></PrivateRoute>} />
                                <Route path="/customer-portal/request-quote" element={<PrivateRoute roles={['customer']}><CustomerQuoteRequestPage /></PrivateRoute>} />
                                <Route path="/customer-portal/invoices/:invoiceId" element={<PrivateRoute roles={['customer']}><CustomerInvoiceDetails /></PrivateRoute>} />
                                <Route path="/customer-portal/quotes" element={<PrivateRoute roles={['customer']}><CustomerQuotesPage /></PrivateRoute>} />
                                <Route path="/customer-portal/quote-requests/:id" element={<PrivateRoute roles={['customer']}><CustomerQuoteDetailsPage /></PrivateRoute>} />
                                <Route path="/customer-portal/quotes/:id" element={<PrivateRoute roles={['customer']}><CustomerQuoteDetailsPage /></PrivateRoute>} />
                                <Route path="/customer-portal/job-history" element={<PrivateRoute roles={['customer']}><CustomerJobHistoryPage /></PrivateRoute>} />
                                <Route path="/customer-portal/job-history/:jobId" element={<PrivateRoute roles={['customer']}><CustomerJobDetailsPage /></PrivateRoute>} />
                                <Route path="/customer-portal/chat" element={<PrivateRoute roles={['customer']}><CustomerChatPage /></PrivateRoute>} />
                                <Route path="/customer-portal/contracts" element={<PrivateRoute roles={['customer']}><CustomerContractsPage /></PrivateRoute>} />
                                <Route path="/customer-portal/contracts/:contractId" element={<PrivateRoute roles={['customer']}><CustomerContractDetailsPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Customer Loyalty Page Route ⭐ */}
                                <Route path="/customer-portal/loyalty" element={<PrivateRoute roles={['customer']}><CustomerLoyaltyPage /></PrivateRoute>} />

                                
                                {/* Staff Portal Routes (No Sidebar) */}
                                <Route path="/staff-dashboard" element={<PrivateRoute roles={['staff']}><StaffDashboard /></PrivateRoute>} />
                                <Route path="/staff-portal/schedule" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffSchedulePage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-payslips" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><MyPayslipsPage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-hours" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><MyHoursPage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-attendance" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><MyAttendancePage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-expenses" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffExpensesPage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-absences" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><MyAbsenceRequestsPage /></PrivateRoute>} />
                                <Route path="/staff-portal/contracts" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffContractsPage /></PrivateRoute>} />
                                <Route path="/staff-portal/contracts/:contractId" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffContractDetailsPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Staff Invoices Routes ⭐ */}
                                <Route path="/staff-portal/my-invoices" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffMyInvoices /></PrivateRoute>} />
                                <Route path="/staff/:staffId/my-invoices" element={<PrivateRoute roles={['admin', 'manager']}><StaffMyInvoices /></PrivateRoute>} />
                                <Route path="/staff/invoices/:invoiceId" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffInvoiceDetails /></PrivateRoute>} />
                                <Route path="/staff-portal/chat" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffChatPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Staff On-Site Quote Calculator Route ⭐ */}
                                <Route path="/staff-portal/quote-calculator" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffQuoteCalculatorPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Staff Training Routes ⭐ */}
                                <Route path="/staff-portal/training" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffTrainingDashboard /></PrivateRoute>} />
                                <Route path="/staff-portal/training/:moduleId" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffTrainingView /></PrivateRoute>} />
                                {/* ⭐ NEW: Staff Reviews Route ⭐ */}
                                <Route path="/staff-portal/my-reviews" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffReviewsPage /></PrivateRoute>} />
                                <Route path="/staff/:staffId/my-reviews" element={<PrivateRoute roles={['admin', 'manager']}><StaffReviewsPage /></PrivateRoute>} />
                                <Route path="/staff-portal/my-rewards" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffRewardsPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Staff Stock Request Routes ⭐ */}
                                <Route path="/staff-portal/order-stock" element={<PrivateRoute roles={['staff', 'admin', 'manager']}><StaffStockRequestPage /></PrivateRoute>} />
                                <Route path="/staff/:staffId/order-stock" element={<PrivateRoute roles={['admin', 'manager']}><StaffStockRequestPage /></PrivateRoute>} />
                                
                                {/* Admin/Manager Routes (Standard Sidebar) */}
                                <Route path="/manager-job-card" element={<PrivateRoute roles={['admin', 'manager']}><StaffDashboard /></PrivateRoute>} />
                                <Route path="/dashboard" element={<PrivateRoute roles={['admin', 'manager']}><Dashboard /></PrivateRoute>} />
                                <Route path="/customers/:customerId?" element={<PrivateRoute roles={['admin', 'manager']}><CustomerPage /></PrivateRoute>} />
                                <Route path="/leads" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><LeadsView /></PrivateRoute>} />
                                
                                {/* * ⭐ NEW: ADMIN TRAINING ROUTES * */}
                                <Route path="/training-modules" element={<PrivateRoute roles={['admin', 'manager']}><AdminTrainingDashboard /></PrivateRoute>} />
                                <Route path="/training-modules/new" element={<PrivateRoute roles={['admin', 'manager']}><AdminModuleEditor /></PrivateRoute>} />
                                <Route path="/training-modules/edit/:moduleId" element={<PrivateRoute roles={['admin', 'manager']}><AdminModuleEditor /></PrivateRoute>} />
                                
                                {/* ⭐ CRITICAL FIX: THE RESULTS PAGE ROUTE ⭐ */}
                                <Route path="/training-modules/:moduleId/results" element={<PrivateRoute roles={['admin', 'manager']}><TrainingResultsTable /></PrivateRoute>} />
                                {/* * ⭐ END NEW: ADMIN TRAINING ROUTES * */}

                                {/* * ⭐ NEW ROUTE FOR RECRUITMENT PIPELINE * */}
                                <Route path="/recruitment" element={<PrivateRoute roles={['admin', 'manager']}><ApplicantDashboard /></PrivateRoute>} />
                                
                                <Route path="/staff" element={<PrivateRoute roles={['admin', 'manager']}><StaffPage staff={staff} /></PrivateRoute>} />
                                {/* * ⭐ NEW ROUTE FOR TIME CLOCK PAGE * */}
                                <Route path="/staff/time-clock" element={<PrivateRoute roles={['admin', 'manager']}><TimeClockReportPage /></PrivateRoute>} />
                                {/* * ⭐ NEW ROUTE FOR PERFORMANCE DASHBOARD * */}
                                <Route path="/staff/performance" element={<PrivateRoute roles={['admin', 'manager']}><StaffPerformanceDashboard /></PrivateRoute>} />
                                {/* * ⭐ NEW ROUTE FOR DAILY ATTENDANCE PAGE * */}
                                <Route path="/staff/daily-attendance" element={<PrivateRoute roles={['admin', 'manager']}><DailyAttendancePage /></PrivateRoute>} />
                                {/* * ⭐ END OF NEW ROUTE * */}
                                <Route path="/form-builder" element={<PrivateRoute roles={['admin', 'manager']}><FormBuilderPage /></PrivateRoute>} />
                                
                                <Route path="/settings" element={<PrivateRoute roles={['admin', 'manager']}><SettingsPage /></PrivateRoute>} />
                                
                                {/* ⭐ NEW ROUTE: Partner Directory Page ⭐ */}
                                <Route path="/partner-directory" element={<PrivateRoute roles={['admin', 'manager']}><PartnerDirectoryPage /></PrivateRoute>} />
                                
                                <Route path="/support" element={<PrivateRoute roles={['admin', 'manager']}><SupportPage /></PrivateRoute>} />
                                <Route path="/scheduler" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><SchedulerView /></PrivateRoute>} />
                                <Route path="/stock" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><StockView /></PrivateRoute>} />
                                <Route path="/assets-renewals" element={<PrivateRoute roles={['admin', 'manager']}><AssetsRenewalsPage /></PrivateRoute>} />
                                <Route path="/coshh-sheets" element={<PrivateRoute roles={['admin', 'manager']}><CoshhSheetsPage /></PrivateRoute>} />
                                <Route path="/site-assessments" element={<PrivateRoute roles={['admin', 'manager']}><SiteAssessmentsPage /></PrivateRoute>} />
                                <Route path="/marketing-reels" element={<PrivateRoute roles={['admin', 'manager']}><MarketingReelsPage /></PrivateRoute>} /> {/* ⭐ NEW: Marketing Reels */}
                                <Route path="/quote-forms" element={<PrivateRoute roles={['admin', 'manager']}><QuoteFormsPage /></PrivateRoute>} /> {/* ⭐ NEW: Quote Forms */}
                                <Route path="/landing-pages" element={<PrivateRoute roles={['admin', 'manager']}><LandingPagesPage /></PrivateRoute>} /> {/* ⭐ NEW: Landing Pages */}
                                <Route path="/spot-checker" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><SpotCheckerPage /></PrivateRoute>} />
                                <Route path="/route-planner" element={<PrivateRoute roles={['admin', 'manager']}><RoutePlannerView /></PrivateRoute>} />
                                <Route path="/staff-absence" element={<PrivateRoute roles={['admin', 'manager']}><StaffAbsencePage /></PrivateRoute>} />
                                <Route path="/absence-reporting" element={<PrivateRoute roles={['admin', 'manager']}><AbsenceReportingPage /></PrivateRoute>} />
                                <Route path="/staff-absences/:staffId" element={<PrivateRoute roles={['admin', 'manager']}><StaffMemberAbsencePage /></PrivateRoute>} />
                                <Route path="/staff-jobs/:staffId" element={<PrivateRoute roles={['admin', 'manager']}><StaffMemberJobsPage /></PrivateRoute>} />
                                <Route path="/message-templates" element={<PrivateRoute roles={['admin']}><EmailTemplatesView /></PrivateRoute>} />
                                
                                {/* ======================================================================================= */}
                                {/* ⭐ FIX: ADDING ALIAS/BASE ROUTES FOR TEMPLATE EDITORS (Necessary to resolve old navigation) */}
                                {/* ======================================================================================= */}
                                <Route path="/email-templates" element={<PrivateRoute roles={['admin', 'manager']}><EmailMarketingPage /></PrivateRoute>} />
                                <Route path="/sms-templates" element={<PrivateRoute roles={['admin', 'manager']}><SmsMarketingPage /></PrivateRoute>} />
                                <Route path="/push-notification-templates" element={<PrivateRoute roles={['admin', 'manager']}><PushNotificationMarketingPage /></PrivateRoute>} />

                                {/* Existing Marketing Routes (keep these, they are the real destination) */}
                                <Route path="/email-marketing" element={<PrivateRoute roles={['admin', 'manager']}><EmailMarketingPage /></PrivateRoute>} />
                                <Route path="/sms-marketing" element={<PrivateRoute roles={['admin', 'manager']}><SmsMarketingPage /></PrivateRoute>} />
                                <Route path="/push-notification-marketing" element={<PrivateRoute roles={['admin', 'manager']}><PushNotificationMarketingPage /></PrivateRoute>} />
                                {/* ======================================================================================= */}


                                <Route path="/funnels" element={<PrivateRoute roles={['admin', 'manager']}><FunnelPage /></PrivateRoute>} />
                                <Route path="/landing-page-builder" element={<PrivateRoute roles={['admin', 'manager']}><LandingPageBuilder /></PrivateRoute>} />
                                <Route path="/invoices" element={<PrivateRoute roles={['admin', 'manager']}><InvoicePage /></PrivateRoute>} />
                                <Route path="/tips" element={<PrivateRoute roles={['admin', 'manager']}><TipsReportPage /></PrivateRoute>} /> {/* ⭐ NEW: customer tips */}
                                <Route path="/invoices/:invoiceId" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><InvoiceDetails /></PrivateRoute>} />
                                <Route path="/payroll" element={<PrivateRoute roles={['admin', 'manager']}><PayrollPage /></PrivateRoute>} />
                                <Route path="/commission-report" element={<PrivateRoute roles={['admin', 'manager']}><CommissionReportPage /></PrivateRoute>} />
                                <Route path="/financial-dashboard" element={<PrivateRoute roles={['admin', 'manager']}><FinancialDashboardPage /></PrivateRoute>} />
                                {/* ⭐ NEW: Company Health Route ⭐ */}
                                <Route path="/company-health" element={<PrivateRoute roles={['admin', 'manager']}><CompanyHealthDashboard /></PrivateRoute>} />
                                <Route path="/jobs" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><JobsPage customers={customers} staff={staff} /></PrivateRoute>} />
                                <Route path="/jobs/:jobId" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><JobDetailsPage /></PrivateRoute>} />
                                <Route path="/admin-chat" element={<PrivateRoute roles={['admin', 'manager']}><AdminChatPage /></PrivateRoute>} />
                                <Route path="/quotes" element={<PrivateRoute roles={['admin', 'manager']}><QuotesPage customers={customers} staff={staff} /></PrivateRoute>} />
                                
                                {/* * Admin Quote Calculator Page * */}
                                <Route path="/quote-calculator" element={<PrivateRoute roles={['admin', 'manager']}><QuoteCalculatorPage staff={staff} /></PrivateRoute>} />
                                
                                <Route path="/admin/quotes/create-from-lead/:quoteRequestId" element={<PrivateRoute roles={['admin', 'manager']}><AdminQuoteGenerationPage /></PrivateRoute>} />
                                <Route path="/admin/quotes/create-from-request/:quoteRequestId" element={<PrivateRoute roles={['admin', 'manager']}><AdminQuoteGenerationPage /></PrivateRoute>} />
                                <Route path="/admin/expenses" element={<PrivateRoute roles={['admin', 'manager']}><AdminExpenseDashboard /></PrivateRoute>} />
                                
                                {/* * ⭐ NEW ROUTE FOR FLEET MANAGEMENT * */}
                                <Route path="/fleet" element={<PrivateRoute roles={['admin', 'manager']}><FleetDashboard /></PrivateRoute>} />
                                
                                <Route path="/knowledge-base" element={<PrivateRoute roles={['admin', 'manager', 'staff']}><KnowledgeBasePage /></PrivateRoute>} />
                                <Route path="/pricing" element={<RecaptchaWrapper><PricingPage /></RecaptchaWrapper>} />

                                <Route path="/owner-dashboard" element={<PrivateRoute><OwnerDashboard /></PrivateRoute>} />

                                {/* * ⭐ CHATBOT CUSTOMIZER ROUTE * */}
                                <Route path="/chatbot-settings" element={<PrivateRoute roles={['admin', 'manager']}><ChatbotCustomizer /></PrivateRoute>} />

                                {/* * ⭐ NEW: REWARD LOTTERY CUSTOMIZER ROUTE * */}
                                <Route path="/admin/lottery-settings" element={<PrivateRoute roles={['admin', 'manager']}><LotteryCustomizer /></PrivateRoute>} />

                                {/* * ⭐ NEW: SMART BOOKING FORM ROUTE (INTERNAL) * */}
                                <Route path="/smart-booking" element={<PrivateRoute roles={['admin', 'manager']}><SmartBookingForm /></PrivateRoute>} />
                                
                                {/* ======================================================================================= */}
                                {/* ⭐ NEW INDUSTRY LANDING PAGE ROUTES (WRAPPED FOR SAFETY) ⭐ */}
                                {/* ======================================================================================= */}
                                <Route path="/plumbers" element={<RecaptchaWrapper><PlumbingLandingPage /></RecaptchaWrapper>} />
                                <Route path="/electricians" element={<RecaptchaWrapper><ElectricianLandingPage /></RecaptchaWrapper>} />
                                <Route path="/landscaping" element={<RecaptchaWrapper><LandscapingLandingPage /></RecaptchaWrapper>} />
                                <Route path="/hvac" element={<RecaptchaWrapper><HvacLandingPage /></RecaptchaWrapper>} />
                                <Route path="/propertymanagement" element={<RecaptchaWrapper><PropertyManagementLandingPage /></RecaptchaWrapper>} />
                                <Route path="/security" element={<RecaptchaWrapper><SecurityLandingPage /></RecaptchaWrapper>} />
                                
                                {/* ⭐ NEW ROUTE: Scratch Card Manager ⭐ */}
                                <Route path="/discounts" element={<PrivateRoute roles={['admin', 'manager']}><ScratchCardManager /></PrivateRoute>} />
                                
                                {/* ========================================================== */}
                                {/* ⭐ AFFILIATE DASHBOARD ROUTE ⭐ */}
                                <Route 
                                    path="/affiliates/dashboard" 
                                    element={<PrivateRoute roles={['admin', 'manager', 'affiliate']}><AffiliateDashboard /></PrivateRoute>} 
                                />
                                {/* ⭐ NEW FRANCHISOR DASHBOARD ROUTE ⭐ */}
                                <Route 
                                    path="/franchisors/dashboard" 
                                    element={<PrivateRoute roles={['admin', 'manager', 'franchisor']}><FranchisorDashboard /></PrivateRoute>} 
                                />
                                {/* ⭐ NEW FRANCHISOR PRICING ROUTE ⭐ */}
                                <Route 
                                    path="/franchisor-pricing" 
                                    element={<PrivateRoute roles={['admin', 'manager', 'franchisor']}><FranchisorPricingPage /></PrivateRoute>} 
                                />
                                {/* ========================================================== */}
                                {/* ⭐ NEW ROUTE: DIRECTORY PREVIEW ⭐ */}
                                <Route path="/directory/preview" element={<PublicDirectoryListing />} />


                                {/* ⭐ FIX: Wrapped Landing Route Loading in Dark Mode friendly container */}
                                <Route
                                    path="/"
                                    element={
                                        authLoading ? (
                                            <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-100 dark:bg-gray-900">
                                                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                                            </div>
                                        ) : user ? (
                                            <Navigate to={getDefaultDashboardPath(user)} replace />
                                        ) : Capacitor.isNativePlatform() ? (
                                            // ⭐ FIX: If Native App, go straight to Login, skip Landing Page entirely
                                            <Navigate to="/login" replace />
                                        ) : (
                                            // Only render Landing Page if on Web
                                            <RecaptchaWrapper>
                                                <LandingPage />
                                            </RecaptchaWrapper>
                                        )
                                    }
                                />

                                <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                        </Suspense>
                    </main>
                </DndProvider>
            </div>
            
            {/* * ⭐ NEW: FLOATING ACTION BUTTON (FOR BOOKING BUDDY) * */}
            {isBookingUser && (
                <button
                    onClick={toggleBookingBuddy}
                    className={`fixed bottom-6 right-6 p-4 rounded-full text-white shadow-2xl transition-all duration-300 transform hover:scale-110 z-50 
                        ${isBookingBuddyOpen ? 'bg-indigo-700' : 'bg-indigo-600'}
                    `}
                    aria-label={isBookingBuddyOpen ? "Close AI Booking Buddy" : "Open AI Booking Buddy"}
                    title="AI Booking Buddy"
                >
                    {isBookingBuddyOpen ? (
                        <XMarkIcon className="h-6 w-6" />
                    ) : (
                        <SparklesIcon className="h-6 w-6" />
                    )}
                </button>
            )}

            {/* * ⭐ NEW: RENDER THE ADMIN BOOKING BUDDY MODAL * */}
            {/* The modal is rendered outside the main content area so it can float correctly */}
            {isBookingUser && (
                <Suspense fallback={null}>
                    <BookingBuddy 
                        isOpen={isBookingBuddyOpen} 
                        onClose={toggleBookingBuddy} 
                        // These props need to be passed for the AI context in BookingBuddy.js
                        staffList={staff} 
                        customersList={customers}
                        jobTypesList={jobTypes}
                        // This triggers a refresh on the scheduler page if job is created successfully
                        onJobCreated={() => queryClient.invalidateQueries({ queryKey: ['scheduler'] })} 
                    />
                </Suspense>
            )}
            {/* * ⭐ END OF BOOKING BUDDY RENDER * */}


            {/* * ⭐ NEW: RENDER THE ADMIN CHATBOT WIDGET (KEPT COMMENTED AS PER ORIGINAL) * */}
            {/* HIDING WIDGET WHILE USER WORKS: Uncomment the block below to re-enable */}
            {/* <Suspense fallback={null}>
                <AdminChatbotWidget />
            </Suspense>
            */}
            {/* * ⭐ END OF NEW WIDGET RENDER * */}

        </div>
    );
}

function App() {
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    // const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY; // <-- REMOVED

    // REMOVED RECAPTCHA CHECK BLOCK

    return (
        // GoogleReCaptchaProvider REMOVED from here
        <ThemeProvider>
            {/* ⭐ CSS FIX: Hide reCAPTCHA Badge on Mobile (prevents overlap with price bar) ⭐ */}
            <style>{`
                @media screen and (max-width: 640px) {
                    .grecaptcha-badge {
                        visibility: hidden !important;
                        opacity: 0 !important;
                    }
                }
            `}</style>

            <QueryClientProvider client={queryClient}>
                <Router>
                    {/* ⭐ FIX: Wrapped Global App Suspense in Dark Mode friendly container */}
                    <Suspense fallback={
                        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
                            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                        </div>
                    }>
                        <AuthProvider>
                            <SocketProvider>
                                <CurrencyProvider>
                                    <SettingsProvider> 
                                        <ChatProvider>
                                            <MapsApiProvider>
                                                {/* ⭐ NEW: NFC Provider wrapping the inner app content ⭐ */}
                                                <NfcProvider>
                                                    <Elements stripe={stripePromise}>
                                                        <ErrorBoundary>
                                                            <AppContent />
                                                        </ErrorBoundary>
                                                    </Elements>
                                                </NfcProvider>
                                            </MapsApiProvider>
                                        </ChatProvider>
                                    </SettingsProvider>
                                </CurrencyProvider>
                            </SocketProvider>
                        </AuthProvider>
                    </Suspense>
                </Router>
                {/* <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                /> */}
            </QueryClientProvider>
        </ThemeProvider>
        // GoogleReCaptchaProvider CLOSING TAG REMOVED from here
    );
}

export default App;